/**
 * ================================================================
 * Bisou Rivage — bisou-vote.js
 * ================================================================
 *
 * Page de vote mensuel. Interface complete generee en JS :
 *   - Galerie photo (grille + vue detail)
 *   - Viewer plein ecran avec navigation metro + swipe
 *   - Systeme de classement top 5 (drag & drop + billes)
 *   - Panneau de selection avec reordonnancement
 *   - Envoi du vote vers Supabase avec tracking de session
 *   - Ecrans : auth, vote ferme, deja vote, confirmation
 *
 * Sections :
 *   CFG + DATA         — Config Liquid, photos, constantes
 *   STATE              — Etat du vote, tracking session
 *   SUPABASE           — API check/insert
 *   HELPERS            — Utilitaires DOM
 *   IMAGE SIZE         — Calcul responsive des dimensions
 *   GRID               — Grille de photos + drag
 *   VIEWER             — Navigation detail, fullscreen, touch
 *   PILL               — Systeme de billes (classement)
 *   BEAD DRAG          — Glisser-deposer des billes
 *   SELECTION          — Panneau top 5 + unranked
 *   CONSIGNE           — Messages de guidage
 *   TRACKING           — Suivi des changements
 *   REFRESH            — Mise a jour globale
 *   HTML BUILD         — Generation du DOM complet
 *   EVENTS             — Binding de tous les listeners
 *   SCREENS            — Ecrans (closed, auth, already-voted, etc.)
 *   INIT               — Point d'entree
 *
 * ================================================================
 */
(function() {
'use strict';

/* ─── CFG + BYPASS ─── */
var CFG = window.BISOU_VOTE_CFG;
if (!CFG) return;
CFG.lang = CFG.lang || {};

/* Décodage des entités HTML numériques + nommées courantes injectées par Shopify `| t`
   (Shopify HTML-escape apostrophes → &#39; avant le | json, donc on décode au chargement).
   Préserve volontairement les tags HTML présents dans les valeurs *_html. */
(function () {
  function decodeEntities(s) {
    if (typeof s !== 'string') return s;
    return s.replace(/&#(\d+);/g, function (_, n) { return String.fromCharCode(parseInt(n, 10)); })
            .replace(/&#x([0-9a-f]+);/gi, function (_, n) { return String.fromCharCode(parseInt(n, 16)); })
            .replace(/&apos;/g, "'")
            .replace(/&quot;/g, '"')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&');
  }
  (function walk(obj) {
    for (var k in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
      var v = obj[k];
      if (typeof v === 'string') obj[k] = decodeEntities(v);
      else if (v && typeof v === 'object') walk(v);
    }
  })(CFG.lang);
})();

if (CFG.bypassAuth && !CFG.customerId) { CFG.customerId='dev-bypass-123'; CFG.email='dev@test.com'; CFG.name='Dev'; }

/* ─── DATA ─── */
var LTRS = 'ABCDEFGHIJ'.split('');
var RC   = ['','#D4AF37','#A0A0A0','#C4845A','rgba(255,255,255,.45)','rgba(255,255,255,.35)'];
var RK_E = ['','🥇','🥈','🥉','4','5'];
var BW   = 46;
var P = (CFG.photos || []);
P.forEach(function(p,i) { p.ltr = LTRS[i]; p._loaded = false; });

/* Auto-detect orientation from actual image dimensions */
function preloadPhotos(callback) {
  var remaining = P.length;
  if (remaining === 0) { callback(); return; }
  P.forEach(function(p) {
    if (!p.urlSm && !p.url) { p._loaded = true; remaining--; if (remaining <= 0) callback(); return; }
    var img = new Image();
    img.onload = function() {
      p.h = img.naturalWidth > img.naturalHeight;
      p._loaded = true;
      remaining--;
      if (remaining <= 0) callback();
    };
    img.onerror = function() {
      p._loaded = true;
      remaining--;
      if (remaining <= 0) callback();
    };
    img.src = p.url;
  });
  /* Safety timeout — don't block forever */
  setTimeout(function() { if (remaining > 0) callback(); }, 5000);
}

/* ─── STATE ─── */
var topVotes   = [null,null,null,null,null];
var vCur       = 0;
var vMode      = 'vote';
var vCtxOpen   = false;
var vRotated   = false;
var previewIds = [];
var previewIdx = 0;
var pd         = null;
var previewVisible = false;

var DT_TOP      = 'bv-top';
var DT_UNRANKED = 'bv-unranked';

var root;

/* ─── SESSION TRACKING ─── */
var sessionStart    = Date.now();
var firstPick       = null;
var firstRank1      = null;
var rank1Changes    = 0;
var lastRank1       = null;
var totalAdditions  = 0;
var totalRemovals   = 0;
var photosViewed    = [];
var viewerTimes     = {};
var viewerOpenAt    = null;
var viewerCurrentId = null;
var source   = (new URLSearchParams(location.search).get('ref')) || 'direct';
var platform = /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop';

/* ─── API VOTE ─── */
/* Le bulletin ne part plus directement dans Supabase. La table `votes` est
   fermee a la cle anonyme depuis la migration 147 : elle exposait les
   bulletins complets de tous les votants, e-mails compris, et acceptait
   n'importe quelle ecriture. Tout passe desormais par le serveur, qui verifie
   la campagne, la fenetre de vote et l'appartenance des choix aux candidates.
   Chaque reponse est lue AVEC son code de statut : un refus silencieux se
   lisait auparavant comme un succes. */
var API = String(CFG.voteApiUrl || '').replace(/\/+$/, '');

function apiStatus(cid, campId) {
  if (!API || !campId) return Promise.reject(new Error('API vote non configuree'));
  return fetch(avecApercu(
    API + '/api/vote/status?campaign_id=' + encodeURIComponent(campId) +
    '&customer_id=' + encodeURIComponent(cid || '')
  )).then(function(r) {
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
  });
}

/* La configuration du scrutin vient du serveur, plus des blocs du thème.
   Jusqu'ici, les dix oeuvres etaient saisies a la main dans l'editeur Shopify —
   dix images, dix titres, dix histoires, plus l'identifiant de campagne — a
   chaque saison. L'app connait tout cela : elle prepare le lot, elle en fige
   l'ordre, elle sait laquelle est ouverte.

   Repli integral : si la route ne repond pas, si elle ne rend aucune campagne,
   ou si le lot est vide, on garde les blocs statiques et l'identifiant du
   reglage. Une panne de configuration ne doit pas vider la page de vote.

   `allowTest` n'est envoye que par un theme de repetition. Le theme publie ne
   verra donc jamais une campagne de test, meme quand elle est la seule
   ouverte. */
/**
 * VOIR UNE REPETITION SANS TOUCHER AU THEME.
 *
 * Le reglage « Autoriser les campagnes de repetition » reste, mais il a un
 * defaut : il faut penser a le decocher, et une case oubliee laisse un scrutin
 * de test visible par les clients. `?repetition=1` fait la meme chose pour la
 * seule personne qui tape l adresse, et disparait au rechargement suivant.
 *
 * Aucun risque de fuite : sans ce parametre et sans la case, la route refuse
 * de servir une campagne de test, meme quand elle est la seule ouverte.
 */
function repetitionAutorisee() {
  if (CFG.allowTest) return true;
  try { return new URLSearchParams(location.search).has('repetition'); } catch (e) { return false; }
}

/**
 * LE LIEN PRIVE D UNE REPETITION — `?apercu=<jeton>`.
 *
 * « MAIS JE VAIS VOIR OU ??? » (Max, 18/08). L app fabrique un lien
 * bisourivage.fr/pages/vote?apercu=<jeton> et le theme ne lisait pas ce
 * parametre : la page repondait « Le prochain vote sera bientot disponible ! »
 * sur une repetition grande ouverte.
 *
 * Le theme n a qu UNE chose a faire : recopier le parametre tel quel sur chacun
 * des trois appels a l app. Tout le controle vit cote serveur — la table le
 * reserve aux campagnes de repetition, la route refuse un jeton revoque ou
 * inconnu. Ici on transporte, on ne juge pas.
 *
 * SANS LE PARAMETRE, RIEN NE CHANGE : `apercuDuLien()` rend une chaine vide et
 * chaque appel repart exactement comme avant.
 */
function apercuDuLien() {
  try {
    var v = new URLSearchParams(location.search).get('apercu');
    return v ? String(v).trim() : '';
  } catch (e) { return ''; }
}

/** Ajoute `apercu` a une adresse d appel, et seulement s il existe. */
function avecApercu(url) {
  var jeton = apercuDuLien();
  if (!jeton) return url;
  return url + (url.indexOf('?') === -1 ? '?' : '&') + 'apercu=' + encodeURIComponent(jeton);
}

function apiConfig() {
  if (!API) return Promise.resolve(null);
  return fetch(avecApercu(API + '/api/vote/config' + (repetitionAutorisee() ? '?allow_test=1' : '')))
    .then(function(r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
    .catch(function(err) { console.warn('Vote config indisponible, repli sur les blocs du theme:', err); return null; });
}

/**
 * Rend un ETAT, plus un booleen. Le booleen d avant confondait trois
 * situations que la page doit distinguer a l ecran :
 *
 *   'adoptee'    une campagne du serveur remplace les reglages du theme
 *   'aucune'     le serveur a repondu : rien n est ouvert. C est une verite.
 *   'refusee'    une repetition tourne, ce theme n a pas le droit de la voir
 *   'injoignable' l app n a pas repondu. On ne sait RIEN de l etat du scrutin.
 *
 * La distinction entre 'aucune' et 'injoignable' est tout l enjeu de la
 * tache 2 : la premiere autorise « le vote est ferme », la seconde l interdit.
 */
function applyServerConfig(cfg) {
  if (!cfg) {
    console.warn('[bisou-vote] L app vote n a pas repondu : etat du scrutin inconnu.');
    return 'injoignable';
  }
  if (cfg.ok === false && !cfg.reason) {
    console.warn('[bisou-vote] L app vote a repondu en erreur : etat du scrutin inconnu.');
    return 'injoignable';
  }
  if (!cfg.campaign || !cfg.campaign.id) {
    if (cfg.reason === 'repetition_non_autorisee') {
      console.warn('[bisou-vote] Une repetition est ouverte mais ce theme ne l affiche pas. Cochez « Autoriser les campagnes de repetition » dans l editeur, sur ce theme non publie.');
      return 'refusee';
    }
    console.info('[bisou-vote] Aucune campagne ouverte : le serveur est formel.');
    return 'aucune';
  }

  /* L IDENTITE ET LES DATES D ABORD, LES OEUVRES ENSUITE.
     Avant, un lot d oeuvres vide faisait sortir la fonction avant meme
     d avoir pris l identifiant de campagne : la page repartait alors sur la
     saison precedente. Or c est exactement ce que renvoie l app pour une
     ouverture programmee — elle retient les photos jusqu a l heure dite.
     L ouverture du 29 aout serait donc tombee sur le scrutin de la saison 7.
     On adopte donc toujours la campagne ; seules les images sont
     conditionnelles. */
  CFG.campaignId = cfg.campaign.id;
  /* C EST LE SERVEUR QUI DIT SI LE SCRUTIN EST OUVERT, PAS L HORLOGE DU
     VISITEUR. `opens_at` ne sert qu a choisir QUEL ecran de fermeture montrer.
     Sans ce drapeau, un decalage d horloge ou de fuseau chez le visiteur
     suffisait a afficher « ouverture prochaine » par-dessus un vote grand
     ouvert, et donc a l empecher de voter. */
  CFG.campaignOpen = cfg.campaign.open === true;
  /* La marque anti-doublon porte l'identifiant de la campagne : elle doit
     suivre celui du serveur, sinon un votant reste bloque par le cookie d'une
     saison precedente, ou repasse a travers celui de la saison en cours. */
  VOTE_KEY = 'bisou_voted_' + String(CFG.campaignId).replace(/[^a-z0-9]/gi, '');
  bvAdopterDates(cfg.campaign.closes_at, cfg.campaign.opens_at);

  /* UNE REPETITION SE DIT. Max a choisi de repeter sur le theme actif : un
     client peut donc tomber sur la page pendant le test. Il doit comprendre en
     une seconde que ce scrutin ne compte pas, plutot que de croire avoir vote.
     Le bandeau ne bloque rien et disparait avec la campagne.

     Deux facons d arriver ici, et le bandeau vaut pour les deux : le lien prive
     `?apercu=`, et le theme de repetition qui envoie `allow_test`. Il n existe
     sur AUCUNE page qui n a ni l un ni l autre : sans eux la route ne sert
     jamais de campagne `is_test`. */
  if (cfg.campaign.is_test || cfg.apercu === true || apercuDuLien()) marquerRepetition();

  if (!cfg.photos || !cfg.photos.length) {
    /* Pas une panne : l ouverture est programmee et l app garde les oeuvres
       sous le coude. On garde les blocs du theme comme decor, la campagne du
       serveur pour la verite. */
    console.info('[bisou-vote] Campagne', cfg.campaign.label, 'adoptee sans oeuvres (ouverture programmee ou lot vide).');
    return 'adoptee';
  }

  console.info('[bisou-vote] Campagne servie par l app :', cfg.campaign.label, '·', cfg.photos.length, 'oeuvre(s)', cfg.campaign.is_test ? '· REPETITION' : '');
  P = cfg.photos.slice(0, 10);
  P.forEach(function(p, i) { p.ltr = LTRS[i]; p._loaded = false; });
  return 'adoptee';
}

/* ─── LES DATES VIENNENT DE LA BASE, PLUS DU THEME ───
   `vote_deadline`, `results_date` et `vote_month` etaient trois reglages
   tapes a la main dans l editeur. Ils portaient encore, le 18 aout, la
   cloture du 30 juillet : le compte a rebours etait donc masque, et il le
   serait reste le 29 aout sans que personne ne le voie. La base sait tout
   cela. Les reglages ne servent plus que de repli quand l app se tait. */
function bvAdopterDates(closesAt, opensAt) {
  var cloture = closesAt ? new Date(closesAt) : null;
  if (cloture && !isNaN(cloture.getTime())) {
    CFG.voteDeadline = closesAt;
    CFG.resultsDate  = bvDateLisible(cloture);
  }
  var ouverture = opensAt ? new Date(opensAt) : null;
  if (ouverture && !isNaN(ouverture.getTime())) {
    CFG.opensAt = opensAt;
    /* La pastille du mois suit l ouverture du scrutin, pas sa cloture : la
       saison 8 court du 29 aout au 28 septembre et se nomme par son debut. */
    CFG.month = bvMoisISO(ouverture);
  } else if (!CFG.opensAt && cloture && !isNaN(cloture.getTime())) {
    /* Repli seulement si l ouverture n a JAMAIS ete connue. /api/vote/status
       renvoie closes_at sans opens_at : sans ce garde-fou, le second appel
       ecrasait le mois deja calcule depuis l ouverture et la pastille passait
       de « 2026-08 » a « 2026-09 » une seconde apres l affichage. */
    CFG.month = bvMoisISO(cloture);
  }
}

var BV_MOIS_FR = ['Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];
var BV_MOIS_EN = ['January','February','March','April','May','June','July','August','September','October','November','December'];

/* « 28 Septembre » / « September 28 » — le format que portait le reglage. */
function bvDateLisible(d) {
  return bvLocalePrefix() === '/en'
    ? BV_MOIS_EN[d.getMonth()] + ' ' + d.getDate()
    : d.getDate() + ' ' + BV_MOIS_FR[d.getMonth()];
}

function bvMoisISO(d) {
  var mo = d.getMonth() + 1;
  return d.getFullYear() + '-' + (mo < 10 ? '0' + mo : String(mo));
}

/* Une ouverture encore a venir : campagne active, heure pas encore atteinte. */
function bvOuvertureFuture() {
  if (!CFG.opensAt) return false;
  var o = new Date(CFG.opensAt);
  return !isNaN(o.getTime()) && o.getTime() > Date.now();
}

function marquerRepetition() {
  if (document.getElementById('bv-repetition')) return;
  var bandeau = document.createElement('div');
  bandeau.id = 'bv-repetition';
  bandeau.setAttribute('role', 'status');
  bandeau.style.cssText = [
    'position:fixed', 'top:0', 'left:0', 'right:0', 'z-index:3000',
    'background:#F2B705', 'color:#1a1a1a',
    "font-family:'Roboto Mono',monospace", 'font-size:11px', 'letter-spacing:.08em',
    'text-transform:uppercase', 'text-align:center', 'padding:7px 12px',
    'box-shadow:0 2px 12px rgba(0,0,0,.35)',
  ].join(';');
  bandeau.textContent = bvLocalePrefix() === '/en'
    ? 'Rehearsal — this vote does not count'
    : 'Repetition — ce vote ne compte pas';
  document.body.appendChild(bandeau);
}

function apiSubmit(email, cid, campId, rankings, meta) {
  if (!API || !campId) return Promise.reject(new Error('API vote non configuree'));
  return fetch(avecApercu(API + '/api/vote/submit'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      campaign_id: campId,
      customer_id: cid,
      email:       email,
      rankings:    rankings,
      meta:        meta
    })
  }).then(function(r) {
    return r.json().catch(function() { return {}; }).then(function(body) {
      if (r.ok && body && body.ok) return body;
      var err = new Error((body && body.code) || ('HTTP ' + r.status));
      err.code = (body && body.code) || '';
      throw err;
    });
  });
}

/* ─── ANTI-DUPLICATE (cookie + localStorage + fingerprint) ─── */
var VOTE_KEY = 'bisou_voted_' + (CFG.campaignId || '').replace(/[^a-z0-9]/gi, '');

function setVotedMark() {
  try {
    var d = new Date(); d.setTime(d.getTime() + 90*24*60*60*1000);
    document.cookie = VOTE_KEY + '=1;expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
  } catch(e) {}
  try { localStorage.setItem(VOTE_KEY, '1'); } catch(e) {}
}

function hasVotedMark() {
  if (document.cookie.indexOf(VOTE_KEY + '=') !== -1) return true;
  try { if (localStorage.getItem(VOTE_KEY) === '1') return true; } catch(e) {}
  return false;
}

function deviceFingerprint() {
  var raw = [
    screen.width, screen.height, screen.colorDepth,
    (typeof Intl !== 'undefined' && Intl.DateTimeFormat) ? Intl.DateTimeFormat().resolvedOptions().timeZone : '',
    navigator.language,
    navigator.hardwareConcurrency || 0,
    new Date().getTimezoneOffset()
  ].join('|');
  var h = 5381;
  for (var i = 0; i < raw.length; i++) {
    h = ((h << 5) + h) + raw.charCodeAt(i);
    h = h & h;
  }
  return 'fp-' + Math.abs(h).toString(36);
}

/* ─── HELPERS ─── */
function esc(s) { var d=document.createElement('div'); d.appendChild(document.createTextNode(s||'')); return d.innerHTML; }
function bvLocalePrefix() {
  var p = window.location.pathname;
  return (p.indexOf('/en/') === 0 || p === '/en') ? '/en' : '';
}
function bvRootUrl() { return bvLocalePrefix() + '/'; }
function bvLatestCollectionUrl() { return CFG.latestCollectionUrl || (bvLocalePrefix() + '/collections/all'); }
function bvFlagSVG(code, cls) {
  cls = cls || 'vhdr-lang-flag';
  if (code === 'fr') {
    return '<svg class="' + cls + '" viewBox="0 0 3 2" aria-hidden="true"><rect width="3" height="2" fill="#fff"/><rect width="1" height="2" fill="#002395"/><rect x="2" width="1" height="2" fill="#ED2939"/></svg>';
  }
  var uid = 'bvgb-' + Math.random().toString(36).slice(2, 8);
  return '<svg class="' + cls + '" viewBox="0 0 60 30" aria-hidden="true"><clipPath id="' + uid + '"><path d="M30,15 h30 v15 z M30,15 v15 H0 z M30,15 h-30 v-15 z M30,15 v-15 h30 z"/></clipPath><path d="M0,0 v30 h60 V0 z" fill="#012169"/><path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" stroke-width="6"/><path d="M0,0 L60,30 M60,0 L0,30" clip-path="url(#' + uid + ')" stroke="#C8102E" stroke-width="4"/><path d="M30,0 v30 M0,15 h60" stroke="#fff" stroke-width="10"/><path d="M30,0 v30 M0,15 h60" stroke="#C8102E" stroke-width="6"/></svg>';
}
function bvLangToggleHTML() {
  var path = window.location.pathname;
  var search = window.location.search || '';
  var isEn = path.indexOf('/en/') === 0 || path === '/en';
  var targetPath = isEn ? (path.replace(/^\/en/, '') || '/') : ('/en' + path);
  /* Affichage du drapeau COURANT (pas la cible) : FR sur site FR, GB sur site EN */
  var currentFlag = bvFlagSVG(isEn ? 'gb' : 'fr');
  var title = isEn ? 'Change language' : 'Changer de langue';
  return '<a href="' + targetPath + search + '" class="vhdr-lang" data-bv-lang-toggle data-target-lang="' + (isEn ? 'fr' : 'en') + '" title="' + title + '" aria-label="' + title + '">' + currentFlag + '</a>';
}
function photo(id)   { return P.find(function(p){return p.id===id;}); }
function rankOf(id)  { var i=topVotes.indexOf(id); return i>=0?i+1:0; }
function active()    { return topVotes.filter(Boolean); }
function unranked()  { return P.filter(function(p){return !topVotes.includes(p.id);}); }
function rc(r)       { return RC[r] || 'rgba(255,255,255,.3)'; }
function isDesktop() { return window.matchMedia('(min-width:1024px)').matches; }

function toast(msg) {
  document.querySelectorAll('.toast').forEach(function(t){t.remove();});
  var t = document.createElement('div');
  t.className='toast'; t.textContent=msg;
  root.appendChild(t);
  setTimeout(function(){t.remove();}, 2600);
}

/* ═══════════════════════════════════
   IMAGE SIZE — computed from DOM
═══════════════════════════════════ */
function updateImgSize() {
  var fw = document.getElementById('vFrameWrap');
  var vi = document.getElementById('vimg');
  var p  = P[vCur];
  if (!fw || !vi || !p) return;

  var rect = fw.getBoundingClientRect();
  if (rect.width < 10 || rect.height < 10) { requestAnimationFrame(updateImgSize); return; }

  var desktop = isDesktop();
  var reserveH = desktop ? 160 : 16;
  /* Mobile vertical : augmenter le reserveV pour éviter que l'image colle au bord haut/bas */
  var reserveV = desktop ? 24 : (p.h ? 4 : 24);

  var availW = rect.width - reserveH;
  var maxH = rect.height - reserveV;

  /* Sur mobile, scale up les images verticales de 18% */
  if (!desktop && !p.h) maxH = Math.min(maxH * 1.55, rect.height);

  var ratio = p.h ? 3/2 : 2/3;
  var w, h;
  if (p.h) {
    w = Math.min(availW, maxH * ratio);
    h = w / ratio;
  } else {
    h = Math.min(maxH, availW / ratio);
    w = h * ratio;
  }

  vi.style.width  = Math.round(w) + 'px';
  vi.style.height = Math.round(h) + 'px';

  /* #fsImg : pas de sizing inline — laisser le CSS (width:100vw / height:auto / max-height / object-fit) gérer
     → l'image rend selon son ratio NATUREL (plus robuste que les ratios 3:2 / 2:3 présumés) */
  var fi = document.getElementById('fsImg');
  if (fi) {
    fi.style.width = '';
    fi.style.height = '';
  }
}

/* ─── GRID ─── */
function buildGrid() {
  var g = document.getElementById('grid');
  g.innerHTML = '';
  var n = active().length;
  var hint = document.getElementById('gridHint');
  if (hint) hint.textContent =
    n>=5 ? CFG.lang.grid_complete : (CFG.lang.grid_progress || '').replace('{total}', P.length).replace('{remaining}', 5-n);

  P.forEach(function(p,i) {
    var r   = rankOf(p.id);
    var div = document.createElement('div');
    div.className = 'gc'+(r?' sel':'');
    div.style.animationDelay = (i*0.03)+'s';

    var imgHtml = p.urlSm
      ? '<img src="' + p.urlSm + '" alt="' + esc(p.t) + '" class="gc-img ' + (p.h?'h':'v') + '" loading="lazy">'
      : '<div class="gc-img ' + (p.h?'h':'v') + ' ' + (p.c||'') + '"></div>';

    div.innerHTML =
      '<div class="gc-img-wrap">' + imgHtml + '</div>' +
      '<div class="gc-footer">' +
        '<span class="gc-ltr">' + p.ltr + '</span>' +
        '<p class="gc-ttl">"' + esc(p.t) + '"</p>' +
      '</div>' +
      (r ? '<div class="gc-badge" style="background:'+rc(r)+';color:#111">' + r + '</div>' : '');
    /* Click opens detail — but not if we just dragged */
    (function(el, idx){
      var wasDragged = false;
      el.addEventListener('dragstart', function(){ wasDragged = true; });
      el.addEventListener('dragend', function(){ setTimeout(function(){ wasDragged = false; }, 50); });
      el.addEventListener('click', function(e){
        if (wasDragged) return;
        e.stopPropagation();
        openViewer(idx, 'vote');
      });
    })(div, i);

    /* Desktop: drag grid card to selection panel — disabled on mobile to keep tap responsive */
    div.draggable = isDesktop();
    (function(photo) {
      div.addEventListener('dragstart', function(e) {
        e.dataTransfer.effectAllowed = 'copy';
        e.dataTransfer.setData(DT_UNRANKED, String(photo.id));
        /* Ghost miniature */
        var ghost = document.createElement('div');
        ghost.style.cssText = 'width:56px;height:56px;border-radius:8px;overflow:hidden;position:fixed;top:-200px;left:-200px;z-index:-1;box-shadow:0 4px 16px rgba(0,0,0,.5)';
        var gImg = document.createElement('img');
        gImg.src = photo.urlSm || photo.url;
        gImg.style.cssText = 'width:100%;height:100%;object-fit:cover';
        ghost.appendChild(gImg);
        document.body.appendChild(ghost);
        e.dataTransfer.setDragImage(ghost, 28, 28);
        setTimeout(function() { document.body.removeChild(ghost); }, 0);
        var ss = document.getElementById('ss');
        if (ss) ss.classList.add('drag-highlight');
      });
      div.addEventListener('dragend', function() {
        var ss = document.getElementById('ss');
        if (ss) ss.classList.remove('drag-highlight');
      });
    })(p);

    g.appendChild(div);
  });
}

function updateGridBar() {
  var n   = active().length;
  var bar = document.getElementById('gbar');
  if (!n) { bar.classList.remove('on'); return; }
  bar.classList.add('on');
  var btn=document.getElementById('gbtn'), msg=document.getElementById('gmsg');
  if (n>=5) { btn.className='vbtn'; btn.textContent=CFG.lang.grid_btn_full; msg.textContent=''; }
  else { btn.className='vbtn part'; btn.textContent=(CFG.lang.grid_btn_partial || '').replace('{n}', n); msg.innerHTML=CFG.lang.grid_warn_top5_required; msg.className='vmsg w'; }
}

/* ─── VIEWER ─── */
function openViewer(idx, mode, pIds, pIdx) {
  if (viewerCurrentId !== null && viewerOpenAt !== null) {
    var delta = Date.now() - viewerOpenAt;
    viewerTimes[viewerCurrentId] = (viewerTimes[viewerCurrentId] || 0) + delta;
  }
  viewerOpenAt = Date.now();
  viewerCurrentId = P[idx] ? P[idx].id : null;
  if (viewerCurrentId && photosViewed.indexOf(viewerCurrentId) === -1) {
    photosViewed.push(viewerCurrentId);
  }
  vCur=idx; vMode=mode; vRotated=false;
  if (mode==='preview') { previewIds=pIds||[]; previewIdx=pIdx||0; }
  renderV(null);
  document.getElementById('sv').classList.add('on');
  setupTouch();
  /* Synchro view toggle — both nav bars */
  if (isDesktop()) {
    ['1','2'].forEach(function(id) {
      var vg = document.getElementById('vtGrid'+id);
      var vd = document.getElementById('vtDetail'+id);
      if (vg && vd) { vg.classList.remove('act'); vd.classList.add('act'); }
    });
  }
}

function closeViewer() {
  if (viewerCurrentId !== null && viewerOpenAt !== null) {
    var delta = Date.now() - viewerOpenAt;
    viewerTimes[viewerCurrentId] = (viewerTimes[viewerCurrentId] || 0) + delta;
    viewerOpenAt = null;
    viewerCurrentId = null;
  }
  document.getElementById('sv').classList.remove('on');
  vCtxOpen = false;
  document.getElementById('vctx').classList.remove('open');
  previewVisible = false;
  /* Synchro view toggle — both nav bars */
  if (isDesktop()) {
    ['1','2'].forEach(function(id) {
      var vg = document.getElementById('vtGrid'+id);
      var vd = document.getElementById('vtDetail'+id);
      if (vg && vd) { vg.classList.add('act'); vd.classList.remove('act'); }
    });
  }
  refresh();
}

function renderV(dir) {
  var isP   = (vMode==='preview');
  var total = isP ? previewIds.length : P.length;
  var pos   = isP ? previewIdx : vCur;
  var p     = P[vCur];
  if (!p) return;

  document.getElementById('vctr').textContent = p.ltr + ' \u00b7 ' + (pos+1) + ' / ' + total;
  document.getElementById('aL').classList.toggle('dis', pos===0);
  document.getElementById('aR').classList.toggle('dis', pos===total-1);

  var pp = document.getElementById('vpp');
  pp.className = 'vpp'+(vRotated?' rot':'');
  if (dir) { void pp.offsetWidth; pp.classList.add(dir==='r'?'el':'er'); }

  /* Viewer image — real <img> inside vpp */
  var vpp = document.getElementById('vpp');
  var vi = document.getElementById('vimg');
  if (p.url) {
    if (vi.tagName !== 'IMG') {
      var img = document.createElement('img');
      img.id = 'vimg';
      img.className = 'vimg bv-vimg-main ' + (p.h ? 'h' : 'v');
      img.style.display = 'block';
      img.style.borderRadius = '1px';
      vpp.replaceChild(img, vi);
      vi = img;
    }
    vi.src = p.url;
    vi.className = 'vimg bv-vimg-main ' + (p.h ? 'h' : 'v');
    vi.alt = p.t || '';
  } else {
    vi.className = 'vimg bv-vimg-main ' + (p.h ? 'h' : 'v') + ' ' + (p.c || '');
  }

  /* Fullscreen image */
  var fsWrap = document.getElementById('fsWrap');
  var fi = document.getElementById('fsImg');
  if (p.url) {
    if (fi.tagName !== 'IMG') {
      var fimg = document.createElement('img');
      fimg.id = 'fsImg';
      fimg.style.maxWidth = '100%';
      fimg.style.maxHeight = '100%';
      fimg.style.objectFit = 'contain';
      fimg.style.display = 'block';
      fsWrap.replaceChild(fimg, fi);
      fi = fimg;
    }
    fi.src = p.url;
    fi.alt = p.t || '';
  } else {
    fi.className = 'vimg ' + (p.c || '');
    fi.style.maxWidth = '100%';
    fi.style.maxHeight = '100%';
  }

  document.getElementById('vctxFull').textContent = p.d;
  document.getElementById('vtitle').textContent   = p.t;

  var r  = rankOf(p.id);
  var st = document.getElementById('vstatus');
  if (r) {
    var lbl=['', CFG.lang.viewer_status_rank_1, CFG.lang.viewer_status_rank_2, CFG.lang.viewer_status_rank_3, CFG.lang.viewer_status_rank_4, CFG.lang.viewer_status_rank_5];
    st.textContent=lbl[r]||(CFG.lang.viewer_status_rank_n || '').replace('{n}', r); st.className='vstatus r';
  } else { st.textContent=CFG.lang.viewer_status_unranked; st.className='vstatus'; }

  var vab = document.getElementById('vAddBtn');
  if (vab) {
    var vtx = vab.querySelector('.vadd-txt'), vic = vab.querySelector('.vadd-ico');
    if (r) { vab.classList.add('is-added'); if(vic)vic.textContent='✓'; if(vtx)vtx.textContent=vab.dataset.added; }
    else { vab.classList.remove('is-added'); if(vic)vic.textContent='+'; if(vtx)vtx.textContent=vab.dataset.add; }
  }

  buildMetroH(pos,total,isP);

  requestAnimationFrame(updateImgSize);

  if (!isP) {
    document.getElementById('pillShell').style.display='';
    document.getElementById('pillNums').style.display='';
    renderPill();
  } else {
    document.getElementById('pillShell').style.display='';
    document.getElementById('pillNums').style.display='';
    renderPreviewPill();
  }
}

/* ─── Metro horizontal ─── */
function buildMetroH(pos,total,isP) {
  var nodes=document.getElementById('metroNodes');
  var fill =document.getElementById('metroFill');
  var count=Math.min(total,10);
  nodes.innerHTML='';
  if (fill) {
    if (pos === 0) {
      fill.style.width = '0px';
    } else {
      var pct = pos / (count - 1);
      var trackEl = document.getElementById('metroTrackWrap');
      if (trackEl) {
        var totalW = trackEl.offsetWidth;
        var diam = 22;
        var lineW = (totalW - diam) * pct;
        fill.style.width = Math.round(lineW) + 'px';
      } else {
        var d2 = 22;
        fill.style.width = 'calc(' + (pct * 100) + '% - ' + (pct * d2) + 'px)';
      }
    }
  }

  for (var i=0; i<count; i++) {
    var wrap=document.createElement('div'); wrap.className='mnode-wrap';
    var d=document.createElement('div');
    d.className='mnode'+(i===pos?' active':'');
    d.textContent=LTRS[i];
    var pid=isP?previewIds[i]:P[i].id;
    var r=rankOf(pid);
    var badge=document.createElement('div');
    badge.className='mnode-badge'+(r?' show':'');
    if (r) { badge.textContent=String(r); badge.style.background=rc(r); }
    (function(idx){
      wrap.addEventListener('click',function(){
        if(isP){previewIdx=idx;vCur=P.findIndex(function(p){return p.id===previewIds[idx];});renderV(null);}
        else{vCur=idx;vRotated=false;renderV(null);}
      });
    })(i);
    wrap.appendChild(d); wrap.appendChild(badge); nodes.appendChild(wrap);
  }
}

/* ─── Navigation ─── */
function vNav(dir) {
  var isP=vMode==='preview', total=isP?previewIds.length:P.length, pos=isP?previewIdx:vCur;
  var next=pos+dir; if(next<0||next>=total)return;
  var pp=document.getElementById('vpp');
  pp.classList.add(dir>0?'xl':'xr');
  setTimeout(function(){
    vRotated=false;
    if(isP){previewIdx=next;vCur=P.findIndex(function(p){return p.id===previewIds[next];});}
    else vCur=next;
    renderV(dir>0?'r':'l');
  },230);
}

function toggleCtx() { vCtxOpen=!vCtxOpen; document.getElementById('vctx').classList.toggle('open',vCtxOpen); }

function setupTouch() {
  var fa=document.getElementById('vFrameWrap');
  if (fa._bvTouchBound) return;
  fa._bvTouchBound = true;
  var pinching=false;
  fa.addEventListener('touchstart', function(e){
    if(e.touches.length>=2){pinching=true;return;}
    pinching=false;
    fa._tx=e.touches[0].clientX;fa._ty=e.touches[0].clientY;fa._moved=false;fa._t=Date.now();
  }, {passive:true});
  fa.addEventListener('touchmove', function(e){
    if(e.touches.length>=2){pinching=true;return;}
    if(!pinching&&(Math.abs(e.touches[0].clientX-fa._tx)>8||Math.abs(e.touches[0].clientY-fa._ty)>8))fa._moved=true;
  }, {passive:true});
  fa.addEventListener('touchend', function(e){
    if(pinching){pinching=false;enterFullscreen();return;}
    /* Ignore if the touch started/ended on a navigation arrow — let the arrow's click handler run */
    var tgt = e.target;
    if (tgt && tgt.closest && tgt.closest('.varr')) return;
    var dx=e.changedTouches[0].clientX-fa._tx;
    var adx=Math.abs(dx),ady=Math.abs(e.changedTouches[0].clientY-fa._ty),dt=Date.now()-fa._t;
    if(!fa._moved&&adx<12&&ady<12&&dt<300){enterFullscreen();return;}
    if(fa._moved&&adx>ady){if(dx<-50)vNav(1);else if(dx>50)vNav(-1);}
  }, {passive:true});
  if (!isDesktop()) document.getElementById('vimg').onclick=function(){enterFullscreen();};
}

/* ─── Fullscreen ─── */
function enterFullscreen() {
  var p=P[vCur];
  var fi=document.getElementById('fsImg');
  if (fi.tagName === 'IMG' && p.url) {
    fi.src = p.url;
  } else {
    fi.className='vimg ' + (p.c||'');
  }
  /* Orientation : p.h booléen (horizontal) → ajoute classe .v pour vertical */
  fi.classList.toggle('v', !p.h);
  fi.classList.toggle('h', !!p.h);
  var wrap = document.getElementById('fsWrap');
  if (wrap) wrap.setAttribute('data-orient', p.h ? 'h' : 'v');
  requestAnimationFrame(updateImgSize);
  document.getElementById('sfs').classList.add('on');
}
function exitFullscreen() { document.getElementById('sfs').classList.remove('on'); }

/* ═══════════════════════════════════
   PILL DOM BUILD
═══════════════════════════════════ */
function buildPillDOM() {
  var shell=document.getElementById('pillShell');
  var delZ =document.getElementById('pillDel');
  shell.innerHTML=''; shell.appendChild(delZ);
  for (var i=0; i<5; i++) {
    var bw=document.createElement('div'); bw.className='bead-wrap'; bw.id='bw'+i;
    var b =document.createElement('div'); b.id='b'+i; b.className='bead empty'; b.textContent='+';
    var tip=document.createElement('div'); tip.className='bead-tooltip'; tip.id='bt'+i;
    bw.appendChild(b); bw.appendChild(tip);
    (function(idx){b.addEventListener('pointerdown',function(e){onBeadDown(e,idx);});})(i);
    shell.appendChild(bw);
  }
}

/* ─── Render vote pill ─── */
function renderPill() {
  var p      = P[vCur];
  var isIn   = topVotes.includes(p.id);
  var isFull = active().length >= 5 && !isIn;

  document.getElementById('pillHint').textContent =
    isFull && !isIn ? CFG.lang.pill_hint_full :
    isIn             ? CFG.lang.pill_hint_in :
                       CFG.lang.pill_hint_default;

  var nums = document.querySelectorAll('#pillNums .bead-rank-num');
  nums.forEach(function(n, i) {
    n.className = 'bead-rank-num';
    if (topVotes[i]) { if(i===0)n.classList.add('rk1'); else if(i===1)n.classList.add('rk2'); else if(i===2)n.classList.add('rk3'); else n.classList.add('rk45'); }
    if (topVotes[i] === p.id) n.classList.add('cur');
  });

  for (var i = 0; i < 5; i++) {
    var id  = topVotes[i];
    var ph  = id ? P.find(function(x){return x.id === id;}) : null;
    var b   = document.getElementById('b'+i);
    var tip = document.getElementById('bt'+i);
    if (!b) continue;

    b.className = 'bead';
    b.onclick = null;

    if (!id) {
      b.classList.add('empty'); b.textContent = '+';
    } else {
      b.classList.add('filled');
      if(i===0)b.classList.add('rk1'); else if(i===1)b.classList.add('rk2'); else if(i===2)b.classList.add('rk3');
      if (id === p.id) {
        b.classList.add('active-bead');
      }
      b.textContent = ph.ltr;
      if (tip) tip.textContent = ph.t;
    }

    (function(idx, bead, tooltip) {
      bead.onmouseenter = function() {
        if (!topVotes[idx] || pd) return;
        var rect = bead.getBoundingClientRect();
        tooltip.style.left = (rect.left + rect.width/2) + 'px';
        tooltip.style.top  = (rect.top - 40) + 'px';
        tooltip.style.transform = 'translateX(-50%)';
        tooltip.classList.add('show');
      };
      bead.onmouseleave = function() { tooltip.classList.remove('show'); };
    })(i, b, tip);

    if (!pd) {
      b.style.transform = '';
      b.style.transition = '';
      b.style.zIndex = '1';
    }
  }
  /* Show/hide check button */
  var pcw = document.getElementById('pillCheckWrap');
  if (pcw) pcw.style.display = active().length >= 5 ? '' : 'none';
}

/* ─── Render preview pill ─── */
function renderPreviewPill() {
  document.getElementById('pillHint').textContent=CFG.lang.pill_hint_preview;
  var nums=document.querySelectorAll('#pillNums .bead-rank-num');
  nums.forEach(function(n,i){n.className='bead-rank-num';if(i===0)n.classList.add('rk1');else if(i===1)n.classList.add('rk2');else if(i===2)n.classList.add('rk3');else n.classList.add('rk45');if(i===previewIdx)n.classList.add('cur');});
  for (var i=0; i<5; i++) {
    var id=previewIds[i],ph=id?P.find(function(x){return x.id===id;}):null;
    var b=document.getElementById('b'+i); if(!b)continue;
    b.className='bead';
    if(!ph){b.classList.add('empty');b.textContent='+';}
    else{
      b.classList.add('filled');
      var r=rankOf(id);if(r===1)b.classList.add('rk1');else if(r===2)b.classList.add('rk2');else if(r===3)b.classList.add('rk3');
      if(i===previewIdx){b.classList.add('active-bead');}
      b.textContent=ph.ltr;
      (function(phIdx,cidx){
        b.onclick=function(){previewIdx=cidx;vCur=phIdx;renderV(null);};
      })(P.findIndex(function(pp){return pp.id===id;}),i);
    }
    if(!pd){b.style.transform='';b.style.transition='';b.style.zIndex='1';}
  }
}

/* ═══════════════════════════════════
   BEAD DRAG
═══════════════════════════════════ */
function onBeadDown(e,slot) {
  e.preventDefault();
  if (!topVotes[slot]) { pillAddToSlot(slot); return; }
  pd={slot:slot,startX:e.clientX,hoverSlot:slot,overDelete:false};

  var b=document.getElementById('b'+slot);
  for(var i=0;i<5;i++){var bi=document.getElementById('b'+i);if(bi)bi.style.transition='none';}
  b.style.zIndex='10';
  b.style.transform='scale(1.18)';

  document.addEventListener('pointermove',onBeadMove,{passive:false});
  document.addEventListener('pointerup',  onBeadUp,  {once:true});
  var pBack = document.getElementById('pillBackBtn'); if(pBack) pBack.style.opacity = '0';
}

function onBeadMove(e) {
  if(!pd)return; e.preventDefault();
  var dx=e.clientX-pd.startX;
  var b=document.getElementById('b'+pd.slot);
  b.style.transform='translateX('+dx+'px) scale(1.18)';

  var tip=document.getElementById('bt'+pd.slot);
  tip.style.left=e.clientX+'px';
  var br=b.getBoundingClientRect();
  tip.style.top=(br.top-44)+'px'; tip.style.transform='translateX(-50%)'; tip.classList.add('show');

  var shellRect=document.getElementById('pillShell').getBoundingClientRect();
  var hs=Math.max(0,Math.min(4,Math.round((e.clientX-shellRect.left-BW/2)/BW)));
  pd.hoverSlot=hs;
  pd.overDelete=(e.clientX>shellRect.right+8);
  var dz=document.getElementById('pillDel');
  dz.classList.toggle('show',true); dz.classList.toggle('hot',pd.overDelete);

  for(var i=0;i<5;i++){
    if(i===pd.slot)continue;
    var ob=document.getElementById('b'+i);
    var tgt=i;
    if(hs<pd.slot&&i>=hs&&i<pd.slot)tgt=i+1;
    if(hs>pd.slot&&i>pd.slot&&i<=hs)tgt=i-1;
    var dist=Math.abs(i-pd.slot), wave=dist===1?1.04:1, tx=(tgt-i)*BW;
    ob.style.transform='translateX('+tx+'px) scale('+wave+')';
  }
}

function onBeadUp() {
  document.removeEventListener('pointermove',onBeadMove);
  if(!pd)return;
  var slot=pd.slot, hoverSlot=pd.hoverSlot, overDelete=pd.overDelete; pd=null;
  document.getElementById('pillDel').classList.remove('show','hot');
  document.querySelectorAll('.bead-tooltip').forEach(function(t){t.classList.remove('show');});

  if(overDelete){ topVotes[slot]=null; }
  else if(hoverSlot!==undefined&&hoverSlot!==slot){
    var item=topVotes[slot]; topVotes.splice(slot,1); topVotes.splice(hoverSlot,0,item);
    while(topVotes.length<5)topVotes.push(null); topVotes=topVotes.slice(0,5);
  }
  for(var i=0;i<5;i++){
    var b=document.getElementById('b'+i); if(!b)continue;
    b.style.transition='transform .5s cubic-bezier(.34,1.36,.64,1)';
    b.style.transform=''; b.style.zIndex='1';
  }
  refresh();
  var pBack = document.getElementById('pillBackBtn'); if(pBack) pBack.style.opacity = '1';
}

function pillAddToSlot(slot) {
  var p=P[vCur];
  if(topVotes.includes(p.id)){
    var cur=topVotes.indexOf(p.id);
    if(topVotes[slot]!==null){toast(CFG.lang.toast_rank_taken);return;}
    topVotes[cur]=null; topVotes[slot]=p.id;
  } else {
    if(topVotes[slot]!==null){toast(CFG.lang.toast_rank_taken);return;}
    if(active().length>=5){
      var sh=document.getElementById('pillShell');
      sh.style.animation='shakeX .35s ease'; setTimeout(function(){sh.style.animation='';},400);
      toast(CFG.lang.pill_hint_full); return;
    }
    topVotes[slot]=p.id;
  }
  refresh();
}

/* ═══════════════════════════════════
   SELECTION
═══════════════════════════════════ */
function getDroppedId(e) {
  var str = e.dataTransfer.getData(DT_UNRANKED) || e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('text');
  if (!str) return null;
  var p = P.find(function(x) { return String(x.id) === String(str); });
  return p ? p.id : null;
}

function buildTop5() {
  var zone=document.getElementById('top5Zone'); zone.innerHTML='';
  var filled=topVotes.filter(Boolean);
  if(filled.length===0){
    zone.innerHTML='<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;gap:7px;padding:28px 8px"><div style="width:26px;height:26px;border-radius:50%;border:1px dashed rgba(255,255,255,.2);display:flex;align-items:center;justify-content:center;color:rgba(255,255,255,.2);font-size:18px;font-weight:200">+</div><p style="font-family:Georgia,serif;font-style:italic;font-size:11px;color:rgba(255,255,255,.2);text-align:center">'+esc(CFG.lang.empty_top5_hint)+'</p></div>';
    setupTop5Drop(zone); return;
  }
  topVotes.forEach(function(id,i){
    if(!id)return;
    var p=photo(id),r=i+1,col=rc(r);
    var slot=document.createElement('div');
    slot.className='s-slot'; slot.dataset.i=i; slot.draggable=isDesktop();
    var tw=p.h?'38px':'22px';
    var rkClass = r===1 ? ' rk1' : r===2 ? ' rk2' : r===3 ? ' rk3' : '';

    var thumbContent = p.urlSm
      ? '<img src="' + p.urlSm + '" style="width:100%;height:100%;object-fit:cover;display:block;border-radius:3px">'
      : '';

    slot.innerHTML='<span class="s-rk'+rkClass+'" style="color:'+col+'">'+(r<=3?RK_E[r]:r)+'</span><div class="s-thumb-wrap"><div class="s-thumb '+(p.c||'')+'" style="width:'+tw+';height:26px">'+thumbContent+'</div></div><div class="s-ltr">'+p.ltr+'</div><p class="s-name">'+esc(p.t)+'</p><div class="s-arrows"><div class="s-arr s-up" data-dir="up"><svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 7L5 3L8 7" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div><div class="s-arr s-down" data-dir="down"><svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 3L5 7L8 3" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div><div class="s-x" data-rm="'+id+'"><svg viewBox="0 0 8 8"><line x1="1" y1="1" x2="7" y2="7"/><line x1="7" y1="1" x2="1" y2="7"/></svg></div>';
    (function(idx){
      slot.querySelector('[data-rm]').addEventListener('click',function(e){e.stopPropagation();topVotes[idx]=null;refresh();});
      slot.querySelector('.s-up').addEventListener('click',function(e){e.stopPropagation();if(idx>0){var prev=idx-1;while(prev>=0&&!topVotes[prev])prev--;if(prev>=0){var tmp=topVotes[prev];topVotes[prev]=topVotes[idx];topVotes[idx]=tmp;}refresh();}});
      slot.querySelector('.s-down').addEventListener('click',function(e){e.stopPropagation();var next=idx+1;while(next<5&&!topVotes[next])next++;if(next<5&&topVotes[next]){var tmp=topVotes[next];topVotes[next]=topVotes[idx];topVotes[idx]=tmp;}else{topVotes[idx]=null;}refresh();});
    })(i);
    slot.addEventListener('dragstart',function(e){e.dataTransfer.effectAllowed='move';e.dataTransfer.setData(DT_TOP,String(i));e.dataTransfer.setData('text',String(id));requestAnimationFrame(function(){slot.classList.add('dragging');});});
    slot.addEventListener('dragend',function(){slot.classList.remove('dragging');});
    slot.addEventListener('dragover',function(e){e.preventDefault();slot.classList.add('dov');});
    slot.addEventListener('dragleave',function(){slot.classList.remove('dov');});
    (function(idx){
      slot.addEventListener('drop',function(e){
        e.preventDefault(); slot.classList.remove('dov');
        var si = parseInt(e.dataTransfer.getData(DT_TOP));
        var incomingId = getDroppedId(e);

        if (!isNaN(si) && si !== idx) {
          /* Swap interne au top 5 */
          var tmp = topVotes[si]; topVotes[si] = topVotes[idx]; topVotes[idx] = tmp;
        } else if (incomingId) {
          /* Drop depuis la grille ou unranked */
          var existingIdx = topVotes.indexOf(incomingId);
          if (existingIdx !== -1) {
            var tmp2 = topVotes[existingIdx]; topVotes[existingIdx] = topVotes[idx]; topVotes[idx] = tmp2;
          } else {
            if (topVotes[idx] === null) {
              topVotes[idx] = incomingId;
            } else {
              topVotes.splice(idx, 0, incomingId);
              topVotes = topVotes.filter(Boolean);
              while(topVotes.length < 5) topVotes.push(null);
              topVotes = topVotes.slice(0,5);
            }
          }
        }
        refresh();
      });
    })(i);
    zone.appendChild(slot);
  });
  if(filled.length<5){var h=document.createElement('div');h.className='add-tirage-hint';var rem=5-filled.length;h.innerHTML='<span>+</span><p class="bv-consigne" style="display:inline;font-size:10px">Encore '+rem+' photo'+(rem>1?'s':'')+' avec +</p>';zone.appendChild(h);}
  setupTop5Drop(zone);
}

function setupTop5Drop(zone) {
  zone.addEventListener('dragover',function(e){e.preventDefault();zone.classList.add('dov');});
  zone.addEventListener('dragleave',function(){zone.classList.remove('dov');});
  zone.addEventListener('drop',function(e){e.preventDefault();zone.classList.remove('dov');var sid=getDroppedId(e);if(!sid||topVotes.includes(sid))return;var em=topVotes.indexOf(null);if(em===-1){toast(CFG.lang.toast_top5_full);return;}topVotes[em]=sid;refresh();});
}

function buildUnranked() {
  var wrap=document.getElementById('unranked'); wrap.innerHTML='';
  var uw=document.getElementById('unrankedWrap');
  uw.ondragover=function(e){e.preventDefault();uw.classList.add('dov');};
  uw.ondragleave=function(){uw.classList.remove('dov');};
  uw.ondrop=function(e){
    e.preventDefault(); uw.classList.remove('dov');
    var si = parseInt(e.dataTransfer.getData(DT_TOP));
    if (!isNaN(si) && si >= 0 && si < 5) {
      topVotes[si] = null;
      refresh();
    }
  };
  unranked().forEach(function(p){
    var row=document.createElement('div'); row.className='urow'; row.draggable=isDesktop();
    var tw=p.h?'38px':'22px';

    var thumbContent = p.urlSm
      ? '<img src="' + p.urlSm + '" style="width:100%;height:100%;object-fit:cover;display:block;border-radius:3px">'
      : '';

    row.innerHTML='<div class="u-thumb-wrap"><div class="u-thumb '+(p.c||'')+'" style="width:'+tw+';height:26px">'+thumbContent+'</div></div><div class="u-ltr">'+p.ltr+'</div><p class="u-name">'+esc(p.t)+'</p><div class="u-add" data-a="'+p.id+'">+</div>';
    row.querySelector('.u-add').addEventListener('click',function(e){e.stopPropagation();var em=topVotes.indexOf(null);if(em===-1){toast(CFG.lang.toast_top5_full);return;}topVotes[em]=p.id;refresh();});
    row.addEventListener('dragstart',function(e){row.classList.add('drag-active');e.dataTransfer.setData(DT_UNRANKED,String(p.id));e.dataTransfer.setData('text',String(p.id));});
    row.addEventListener('dragend',function(){row.classList.remove('drag-active');});
    row.addEventListener('dragover',function(e){e.preventDefault();row.classList.add('dov');});
    row.addEventListener('dragleave',function(){row.classList.remove('dov');});
    row.addEventListener('drop',function(e){e.preventDefault();row.classList.remove('dov');});
    wrap.appendChild(row);
  });
}

function updateSelFoot() {
  var n=active().length,btn=document.getElementById('svbtn'),msg=document.getElementById('svmsg');
  var vis=document.getElementById('visBtn'),lbl=document.getElementById('visLabel');
  vis.style.display=n>0?'flex':'none'; if(lbl)lbl.textContent=(CFG.lang.label_top_n || '').replace('{n}', n);
  if(n>=5){
    btn.className='vbtn is-complete';
    btn.innerHTML='<svg class="bv-check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="5 12 10 17 20 7"/></svg><span>'+esc(CFG.lang.btn_validate_full)+'</span>';
    msg.textContent='';msg.className='vmsg';btn.style.opacity='';btn.style.pointerEvents='';btn.style.flexBasis='62%';
  }
  else if(n>=3){
    btn.className='vbtn';btn.textContent=(CFG.lang.btn_validate_partial || '').replace('{n}', n);
    msg.innerHTML='';
    var row=document.createElement('div');
    row.style.cssText='display:flex;align-items:center;justify-content:center;gap:6px';
    var sp=document.createElement('span');
    sp.className='vmsg w';sp.style.cssText='cursor:pointer;text-decoration:none';
    sp.innerHTML=CFG.lang.gift_eligibility_warn;
    sp.addEventListener('click',function(){openSheet('rewards');});
    var qi=document.createElement('div');
    qi.style.cssText='width:15px;height:15px;border-radius:50%;border:1px solid rgba(212,175,55,.4);background:rgba(212,175,55,.08);display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;font-family:Roboto Mono,monospace;font-size:8px;color:rgba(212,175,55,.8);font-weight:600';
    qi.textContent='?';qi.title=CFG.lang.learn_more;
    qi.addEventListener('click',function(){openSheet('rewards');});
    row.appendChild(sp);row.appendChild(qi);
    msg.appendChild(row);msg.className='vmsg';
    btn.style.opacity='';btn.style.pointerEvents='';btn.style.flexBasis='62%';
  }
  else if(n>0){btn.className='vbtn part';btn.textContent=(CFG.lang.btn_pick_more || '').replace('{n}', 3-n);msg.textContent='';btn.style.opacity='.35';btn.style.pointerEvents='none';btn.style.flexBasis='62%';}
  else{btn.className='vbtn part';btn.textContent=CFG.lang.btn_rate_first;btn.style.opacity='.25';btn.style.pointerEvents='none';msg.textContent='';}
}

/* ─── handleValidate : ouvre une modale custom (remplace confirm() natif) ─── */
function handleValidate() {
  var n = active().length;
  if (n < 3) return;

  /* Évite les doublons si l'utilisateur clique plusieurs fois */
  if (document.getElementById('bisou-confirm-modal')) return;

  var modal = document.createElement('div');
  modal.id = 'bisou-confirm-modal';
  modal.style.cssText = 'position:fixed;inset:0;z-index:2000;background:rgba(0,0,0,.8);backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center';
  modal.innerHTML =
    '<div style="background:#1c1c1c;border:1px solid rgba(255,255,255,.1);border-radius:12px;padding:24px;text-align:center;max-width:320px;margin:0 20px;box-shadow:0 16px 48px rgba(0,0,0,.6);animation:fadeUp .2s ease">' +
      '<p style="font-family:Georgia,serif;font-style:italic;font-size:calc(18px * var(--ts));margin:0 0 12px;color:#fff">' + esc((CFG.lang.confirm_modal_title || '').replace('{n}', n)) + '</p>' +
      '<p style="font-size:calc(11px * var(--ts));color:rgba(255,255,255,.6);margin:0 0 24px;line-height:1.5">' + esc(CFG.lang.confirm_modal_body) + '</p>' +
      '<div style="display:flex;gap:10px">' +
        '<button id="bv-confirm-cancel" style="flex:1;padding:10px;border-radius:8px;border:1px solid rgba(255,255,255,.15);background:rgba(255,255,255,.05);color:#fff;font-family:\'Roboto Mono\',monospace;font-size:calc(9px * var(--ts));text-transform:uppercase;letter-spacing:.05em;cursor:pointer">' + esc(CFG.lang.confirm_modal_cancel) + '</button>' +
        '<button id="bv-confirm-ok" style="flex:1;padding:10px;border-radius:8px;border:none;background:#fff;color:#111;font-family:\'Roboto Mono\',monospace;font-size:calc(9px * var(--ts));font-weight:600;text-transform:uppercase;letter-spacing:.05em;cursor:pointer">' + esc(CFG.lang.confirm_modal_ok) + '</button>' +
      '</div>' +
    '</div>';
  document.body.appendChild(modal);

  modal.querySelector('#bv-confirm-cancel').addEventListener('click', function() {
    modal.remove();
  });
  modal.querySelector('#bv-confirm-ok').addEventListener('click', function() {
    modal.remove();
    executeVoteSubmission(n);
  });
}

/* ─── executeVoteSubmission : logique d'envoi Supabase ─── */
function executeVoteSubmission(n) {
  var rankings = {};
  topVotes.forEach(function(id, i) { if (id) rankings[i+1] = id; });

  var btn = document.getElementById('svbtn');
  btn.textContent = CFG.lang.btn_sending;
  btn.style.pointerEvents = 'none';

  var meta = {
    session_duration_s: Math.round((Date.now() - sessionStart) / 1000),
    first_pick:         firstPick,
    first_rank1:        firstRank1,
    rank1_changes:      rank1Changes,
    total_additions:    totalAdditions,
    total_removals:     totalRemovals,
    nb_ranked:          n,
    photos_viewed:      photosViewed,
    viewer_time_per_photo: viewerTimes,
    platform:           platform,
    source:             source,
    voted_at_hour:      new Date().getHours()
  };

  meta.device_fp = deviceFingerprint();

  apiSubmit(CFG.email, CFG.customerId, CFG.campaignId, rankings, meta)
    .then(function() {
      setVotedMark();
      showConfirmation(rankings);
    })
    .catch(function(err) {
      /* Un second envoi n'est pas une panne : c'est un rechargement ou un
         deuxieme onglet. On le dit, on ne demande pas de reessayer. */
      if (err && err.code === 'DEJA_VOTE') { setVotedMark(); showAlreadyVoted(null); return; }
      if (err && (err.code === 'VOTE_FERME' || err.code === 'VOTE_PAS_ENCORE_OUVERT')) { showClosed(); return; }
      console.error('Vote submit error:', err);
      btn.textContent = CFG.lang.btn_error_retry;
      btn.style.pointerEvents = '';
      toast(CFG.lang.toast_submit_error);
    });
}

/* ─── openPreview ─── */
function openPreview() {
  if (previewVisible && document.getElementById('sv').classList.contains('on') && vMode === 'preview') {
    previewVisible = false;
    closeViewer();
    var lbl = document.getElementById('visLabel');
    if (lbl) lbl.textContent = (CFG.lang.label_top_n || '').replace('{n}', active().length);
    return;
  }
  var ids = topVotes.filter(Boolean);
  if (!ids.length) return;
  previewVisible = true;
  var idx = P.findIndex(function(p){return p.id === ids[0];});
  openViewer(idx, 'preview', ids, 0);
  var lbl = document.getElementById('visLabel');
  if (lbl) lbl.textContent = CFG.lang.label_hide_preview;
}

/* ─── TABS (mobile) ─── */
function goTab(t) {
  if (isDesktop()) return;
  var toSel = (t === 'sel');
  var sg = document.getElementById('sg');
  var ss = document.getElementById('ss');

  sg.classList.toggle('out', toSel);
  ss.classList.toggle('on',  toSel);
  document.getElementById('tg').classList.toggle('act', !toSel);
  document.getElementById('ts').classList.toggle('act',  toSel);

  if (toSel) {
    ss.style.opacity = '1'; ss.style.pointerEvents = 'all';
  } else {
    ss.style.opacity = '0'; ss.style.pointerEvents = 'none';
    sg.style.opacity = '1'; sg.style.pointerEvents = 'all'; sg.style.transform = '';
  }
}

/* ─── SHEETS ─── */
function openSheet(id)  { document.getElementById('ov-'+id).classList.add('on'); }
function closeSheet(id) { document.getElementById('ov-'+id).classList.remove('on'); }

/* ─── LAYOUT ─── */
function applyLayout() {
  if (isDesktop()) {
    var sg = document.getElementById('sg');
    sg.classList.remove('out');
    sg.style.cssText = '';
    var ss = document.getElementById('ss');
    ss.style.cssText = '';
    document.getElementById('lw').style.cssText = '';
  } else {
    document.getElementById('lw').style.cssText = '';
    var ss = document.getElementById('ss');
    if (!ss.classList.contains('on')) {
      ss.style.opacity  = '0';
      ss.style.pointerEvents = 'none';
    }
    var sg = document.getElementById('sg');
    if (!sg.classList.contains('out')) {
      sg.style.opacity = '1';
      sg.style.pointerEvents = 'all';
      sg.style.transform = '';
    }
  }
}

/* ─── CONSIGNE ─── */
function updateConsigne() {
  var n = active().length;
  var r = 5 - n;
  var CONSIGNE_COMPLETE = CFG.lang.consigne_complete;
  /* Desktop viewer */
  var cv = document.getElementById('bvConsigne');
  if (cv) {
    if (n >= 5) cv.textContent = CONSIGNE_COMPLETE;
    else if (n > 0) cv.textContent = (CFG.lang.consigne_viewer_partial || '').replace('{n}', n);
    else cv.textContent = CFG.lang.consigne_viewer_empty;
  }
  /* Desktop gallery */
  var cg = document.getElementById('bvConsigneGrid');
  if (cg) {
    if (n >= 5) cg.textContent = CONSIGNE_COMPLETE;
    else if (n > 0) cg.textContent = (CFG.lang.consigne_grid_partial || '').replace('{n}', n);
    else cg.textContent = CFG.lang.consigne_grid_empty;
  }
  /* Mobile grid */
  var mg = document.getElementById('bvConsigneMobileGrid');
  if (mg) {
    if (n >= 5) mg.textContent = CONSIGNE_COMPLETE;
    else if (n > 0) mg.textContent = (CFG.lang.consigne_mobile_grid_partial || '').replace('{n}', n).replace('{remaining}', r);
    else mg.textContent = CFG.lang.consigne_mobile_grid_empty;
  }
  /* Mobile viewer */
  var mv = document.getElementById('bvConsigneMobileViewer');
  if (mv) {
    if (n >= 5) mv.textContent = CFG.lang.consigne_mobile_complete;
    else if (n > 0) {
      var tpl = CFG.lang.consigne_mobile_viewer_partial || {};
      var str = (r === 1 && tpl.one) ? tpl.one : (tpl.other || '');
      mv.textContent = str.replace('{count}', n).replace('{remaining}', r);
    }
    else mv.textContent = CFG.lang.consigne_mobile_viewer_empty;
  }
}

/* ─── TRACKING ─── */
var _prevTop = [null,null,null,null,null];
function trackChanges() {
  var cur = topVotes.slice();
  var prevSet = _prevTop.filter(Boolean);
  var curSet  = cur.filter(Boolean);
  /* Additions: in cur but not in prev */
  curSet.forEach(function(id) { if (!prevSet.includes(id)) { totalAdditions++; if (!firstPick) firstPick = id; } });
  /* Removals: in prev but not in cur */
  prevSet.forEach(function(id) { if (!curSet.includes(id)) totalRemovals++; });
  /* Rank 1 changes */
  if (cur[0] && cur[0] !== _prevTop[0]) {
    if (!firstRank1) firstRank1 = cur[0];
    if (lastRank1 && lastRank1 !== cur[0]) rank1Changes++;
    lastRank1 = cur[0];
  }
  _prevTop = cur;
}

/* ─── REFRESH ─── */
function refresh() {
  trackChanges();
  var n = active().length;
  document.getElementById('tdot').style.display = n > 0 ? 'inline-block' : 'none';
  updateConsigne();
  buildGrid(); updateGridBar();
  buildTop5(); buildUnranked(); updateSelFoot();
  if (document.getElementById('sv').classList.contains('on')) {
    var isP   = (vMode === 'preview');
    var pos   = isP ? previewIdx : vCur;
    var total = isP ? previewIds.length : P.length;
    buildMetroH(pos, total, isP);
    if (vMode === 'vote') { renderPill(); renderV(null); }
  }
}

/* ═══════════════════════════════════
   buildVoteHTML — exact HTML from v14c lines 484-709
═══════════════════════════════════ */
function buildVoteHTML() {
  var m = esc(CFG.month);
  var rd = esc(CFG.resultsDate);
  return '' +
/* ═══ LEFT: gallery + viewer ═══ */
'<div id="lw">' +

  /* GALLERY */
  '<div id="sg">' +
    /* Desktop nav bar */
    '<div class="desktop-nav-bar" style="justify-content:space-between;width:100%">' +
      '<div style="display:flex;align-items:center;gap:12px">' +
        '<div class="bv-exit-btn" title="'+esc(CFG.lang.btn_exit_title)+'" style="width:28px;height:28px;border-radius:50%;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.12);display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;padding:0;margin:0"><svg width="12" height="12" viewBox="0 0 24 24" stroke="rgba(255,255,255,.5)" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></div>' +
        '<div class="bv-view-toggle">' +
          '<button class="bv-vt-btn act" id="vtGrid1"><svg viewBox="0 0 16 16"><rect x="1" y="1" width="6" height="6" rx="1.5"/><rect x="9" y="1" width="6" height="6" rx="1.5"/><rect x="1" y="9" width="6" height="6" rx="1.5"/><rect x="9" y="9" width="6" height="6" rx="1.5"/></svg>'+esc(CFG.lang.tab_all_images)+'</button>' +
          '<button class="bv-vt-btn" id="vtDetail1"><svg viewBox="0 0 16 16"><circle cx="8" cy="8" r="4"/></svg>'+esc(CFG.lang.btn_detail_view)+'</button>' +
        '</div>' +
      '</div>' +
      '<p class="bv-consigne" id="bvConsigneGrid"></p>' +
    '</div>' +
    /* Mobile nav */
    '<div class="nav mobile-nav" style="padding:12px 16px 6px">' +
      '<div style="display:flex;align-items:center;gap:10px">' +
        '<div class="bv-exit-btn" title="'+esc(CFG.lang.btn_exit_title)+'" style="width:24px;height:24px;border-radius:50%;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.15);display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;padding:0;margin:0"><svg width="12" height="12" viewBox="0 0 24 24" stroke="rgba(255,255,255,.6)" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></div>' +
        (CFG.logoUrl ? '<img class="logo-img" src="'+CFG.logoUrl+'" alt="Bisou Rivage">' : '<span class="logo">Bisou Rivage</span>') +
      '</div>' +
      '<div style="display:flex;align-items:center;gap:6px">' +
        '<span class="mpill-tag">' + m + '</span>' +
        bvLangToggleHTML() +
        '<div style="width:26px;height:26px;border-radius:50%;flex-shrink:0;aspect-ratio:1/1;background:var(--r1);color:#111;display:flex;align-items:center;justify-content:center;font-family:\'Roboto Mono\',monospace;font-size:11px;font-weight:600">' + (CFG.name ? CFG.name.charAt(0).toUpperCase() : 'U') + '</div>' +
      '</div>' +
    '</div>' +
    '<div class="bv-sep">&#8203;</div>' +
    '<div class="tabs">' +
      '<button class="tab act" id="tg">'+esc(CFG.lang.tab_all_images)+'</button>' +
      '<button class="tab" id="ts"><span class="tdot" id="tdot" style="display:none"></span>'+esc(CFG.lang.tab_my_selection)+'</button>' +
    '</div>' +
    '<p class="bv-consigne bv-consigne-mobile" id="bvConsigneMobileGrid" style="display:block;text-align:center;padding:6px 16px 2px;margin:0;font-size:9px"></p>' +
    '<div class="gscroll"><div class="bvgrid" id="grid"></div></div>' +
    '<div class="gbar" id="gbar">' +
      '<button class="vbtn" id="gbtn">\u2014</button>' +
      '<p class="vmsg" id="gmsg"></p>' +
    '</div>' +
  '</div>' +

  /* VIEWER */
  '<div id="sv">' +
    /* Desktop nav bar (viewer) */
    '<div class="desktop-nav-bar" style="justify-content:space-between;width:100%">' +
      '<div style="display:flex;align-items:center;gap:12px">' +
        '<div class="bv-close-viewer" id="bvCloseViewer" title="'+esc(CFG.lang.btn_back_gallery_title)+'" style="width:28px;height:28px;border-radius:50%;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.12);display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;padding:0;margin:0"><svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="rgba(255,255,255,.5)" stroke-width="1.8" stroke-linecap="round"><line x1="2" y1="2" x2="8" y2="8"/><line x1="8" y1="2" x2="2" y2="8"/></svg></div>' +
        '<div class="bv-view-toggle">' +
          '<button class="bv-vt-btn" id="vtGrid2"><svg viewBox="0 0 16 16"><rect x="1" y="1" width="6" height="6" rx="1.5"/><rect x="9" y="1" width="6" height="6" rx="1.5"/><rect x="1" y="9" width="6" height="6" rx="1.5"/><rect x="9" y="9" width="6" height="6" rx="1.5"/></svg>'+esc(CFG.lang.btn_grid_view)+'</button>' +
          '<button class="bv-vt-btn act" id="vtDetail2"><svg viewBox="0 0 16 16"><circle cx="8" cy="8" r="4"/></svg>'+esc(CFG.lang.btn_detail_short)+'</button>' +
        '</div>' +
      '</div>' +
      '<span class="vctr" id="vctr" style="display:none"></span>' +
      '<p class="bv-consigne" id="bvConsigne"></p>' +
    '</div>' +
    '<div class="vhdr">' +
      '<div class="vhdr-close">' +
        '<div class="icon-btn" id="vCloseBtn" style="width:30px;height:30px">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.55)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6,9 12,15 18,9"/></svg>' +
        '</div>' +
      '</div>' +
      '<div class="vhdr-mid"><p class="bv-consigne bv-consigne-mobile" id="bvConsigneMobileViewer" style="display:block;font-size:8px;margin:0;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap"></p></div>' +
      '<div class="vhdr-right">' +
        '<div class="hdr-price-vote" style="display:flex;gap:4px;align-items:center">' +
          bvLangToggleHTML() +
          '<div class="hdr-action" id="hdrRewards"><div class="hdr-i">i</div><span>🎁</span></div>' +
          '<div class="hdr-action vote-btn" id="hdrRules"><div class="hdr-i">ℹ</div><span>'+esc(CFG.lang.hdr_rules)+'</span></div>' +
        '</div>' +
      '</div>' +
    '</div>' +

    /* Metro: horizontal */
    '<div class="metro-zone">' +
      '<div class="metro-track-wrap" id="metroTrackWrap">' +
        '<div class="metro-inner">&#8203;' +
          '<div class="metro-line-fill" id="metroFill">&#8203;</div>' +
          '<div class="metro-nodes" id="metroNodes">&#8203;</div>' +
        '</div>' +
      '</div>' +
    '</div>' +

    /* Histoire volet */
    '<div class="vctx" id="vctx">' +
      '<div class="vctx-bar" id="vctxBar">' +
        '<span class="vctx-label">'+esc(CFG.lang.viewer_context_label)+'</span>' +
        '<span class="vctx-chev" id="vctxChev">▾</span>' +
      '</div>' +
      '<div class="vctx-body"><p class="vctx-full" id="vctxFull">—</p></div>' +
    '</div>' +

    /* Photo area — image only, centered */
    '<div class="vphoto-area">' +
      '<div class="vframe-wrap" id="vFrameWrap">' +
        '<div class="vpp" id="vpp">' +
          '<div class="vimg c1" id="vimg"></div>' +
        '</div>' +
      '</div>' +
    '</div>' +

    /* Title + status + expand — outside vphoto-area */
    '<div class="vtitle-row">' +
      '<p class="vtitle" id="vtitle">\u2014</p>' +
      '<p class="vstatus" id="vstatus"></p>' +
      '<div class="vact-btn" id="vExpandBtn" style="position:absolute; right:12px;"><svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M1 4V1h3M8 1h3v3M11 8v3H8M4 11H1V8"/></svg></div>' +
    '</div>' +

    /* Barre nav viewer — fleches + ajouter (sous l image, plus sur l image) */
    '<div class="vnav-bar">' +
      '<button class="vnav-arr l dis" id="aL" aria-label="'+esc(CFG.lang.btn_prev || 'Précédent')+'"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg></button>' +
      '<button class="vadd-btn" id="vAddBtn" data-add="'+(bvLocalePrefix()==='/en'?'Add to my selection':'Ajouter à ma sélection')+'" data-added="'+(bvLocalePrefix()==='/en'?'In my selection':'Dans ma sélection')+'"><span class="vadd-ico">+</span><span class="vadd-txt">'+(bvLocalePrefix()==='/en'?'Add to my selection':'Ajouter à ma sélection')+'</span></button>' +
      '<button class="vnav-arr r" id="aR" aria-label="'+esc(CFG.lang.btn_next || 'Suivant')+'"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></button>' +
    '</div>' +

    /* PILL */
    '<div class="pill-area">' +
      '<div class="pill-nums-row" id="pillNums">' +
        '<div class="bead-rank-num">1</div>' +
        '<div class="bead-rank-num">2</div>' +
        '<div class="bead-rank-num">3</div>' +
        '<div class="bead-rank-num">4</div>' +
        '<div class="bead-rank-num">5</div>' +
      '</div>' +
      '<div class="pill-grid">' +
        '<div class="pill-check-wrap" id="pillCheckWrap" style="display:none"><div class="pill-check" id="pillCheck"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#111" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="2.5 7 5.5 10.5 11.5 3.5"/></svg></div></div>' +
        '<div class="pill-shell" id="pillShell">' +
          '<div class="pill-del" id="pillDel">' +
            '<svg viewBox="0 0 14 14"><line x1="2" y1="2" x2="12" y2="12"/><line x1="12" y1="2" x2="2" y2="12"/></svg>' +
          '</div>' +
        '</div>' +
        '<div class="pill-back" id="pillBackBtn">' +
          '<svg viewBox="0 0 12 12"><polyline points="2,4 6,8 10,4"/></svg>' +
        '</div>' +
      '</div>' +
      '<p class="pill-hint" id="pillHint">'+esc(CFG.lang.pill_hint_default)+'</p>' +
    '</div>' +
  '</div><!-- /sv -->' +

'</div><!-- /lw -->' +

/* ═══ SELECTION ═══ */
'<div id="ss">' +
  '<div class="sel-hdr">' +
    '<div style="display:flex;align-items:center;justify-content:space-between;padding:10px 16px">' +
      '<div style="display:flex;align-items:center;gap:8px">' +
        (CFG.logoUrl ? '<img class="logo-img" src="'+CFG.logoUrl+'" alt="Bisou Rivage">' : '<span class="logo">Bisou Rivage</span>') +
      '</div>' +
      '<div style="display:flex;align-items:center;gap:6px">' +
        '<span class="mpill-tag">' + m + '</span>' +
        bvLangToggleHTML() +
        '<div style="width:26px;height:26px;border-radius:50%;flex-shrink:0;aspect-ratio:1/1;background:var(--r1);color:#111;display:flex;align-items:center;justify-content:center;font-family:\'Roboto Mono\',monospace;font-size:11px;font-weight:600">' + (CFG.name ? CFG.name.charAt(0).toUpperCase() : 'U') + '</div>' +
      '</div>' +
    '</div>' +
    /* Titre + pills sur la même ligne */
    '<div style="display:flex;align-items:center;justify-content:space-between;padding:10px 18px 4px">' +
      '<p class="sel-title" style="margin:0">'+esc(CFG.lang.tab_my_selection)+'</p>' +
      '<div class="sel-pills" style="display:flex;gap:6px">' +
        '<div class="gift-pill" id="giftPill"><div class="info-i">i</div><span class="gift-ico">🎁</span><span class="gift-pill-txt">'+esc(CFG.lang.pill_draw)+'</span></div>' +
        '<div class="info-pill" id="infoPill"><div class="info-i-grey">i</div><span class="info-pill-txt">'+esc(CFG.lang.pill_about)+'</span></div>' +
      '</div>' +
    '</div>' +
  '</div>' +
  '<div class="tabs" style="padding-bottom:8px">' +
    '<button class="tab" id="ssTabGrid">'+esc(CFG.lang.tab_all_images)+'</button>' +
    '<button class="tab act" id="ssTabSel"><span class="tdot"></span>'+esc(CFG.lang.tab_my_selection)+'</button>' +
  '</div>' +
  '<div class="sel-body">' +
    '<p class="sel-sub" style="margin:6px 0 8px; width:100%; text-align:center; color:rgba(255,255,255,.3);">'+esc(CFG.lang.sel_drag_hint)+'</p>' +
    '<div class="top5-zone" id="top5Zone"></div>' +
    '<div class="s-sep"><div class="sep-l">&#8203;</div><span class="sep-t">'+esc(CFG.lang.sel_unranked_label)+'</span><div class="sep-l d">&#8203;</div></div>' +
    '<div id="unrankedWrap"><div class="unranked" id="unranked"></div></div>' +
  '</div>' +
  '<div class="sel-foot">' +
    '<div class="sel-btns" style="align-items:flex-end">' +
      '<button class="vbtn" id="svbtn" style="flex:1">—</button>' +
      '<div style="display:flex;flex-direction:column;align-items:center;gap:3px;flex-basis:36%;flex-shrink:0">' +
        '<p class="visualiser-label" style="margin:0">'+esc(CFG.lang.sel_visualize_label)+'</p>' +
        '<button class="vis-btn" id="visBtn" style="display:none;width:100%;margin:0">' +
          '<svg viewBox="0 0 12 12"><circle cx="6" cy="6" r="4"/><circle cx="6" cy="6" r="1.5" fill="currentColor"/></svg>' +
          '<span id="visLabel">'+esc(CFG.lang.sel_visualize_btn)+'</span>' +
        '</button>' +
      '</div>' +
    '</div>' +
    '<p class="vmsg" id="svmsg"></p>' +
    '<div class="bv-sep" style="margin:10px 0 6px">&#8203;</div>' +
    '<button class="bv-exit-btn" id="exitBtnSel"><svg viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>'+esc(CFG.lang.btn_exit_full)+'</button>' +
  '</div>' +
'</div>' +

/* Fullscreen */
'<div id="sfs">' +
  '<div class="fs-exit" id="fsExit">' +
    '<svg viewBox="0 0 10 10" fill="none" stroke="rgba(255,255,255,.7)" stroke-width="2" stroke-linecap="round"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg>' +
  '</div>' +
  /* Bouton pivoter fullscreen */
  '<div id="fsRotBtn" style="position:absolute;top:max(24px,env(safe-area-inset-top,12px));right:60px;width:32px;height:32px;border-radius:50%;background:rgba(255,255,255,.1);border:1px solid rgba(255,255,255,.16);display:flex;align-items:center;justify-content:center;cursor:pointer;z-index:5">' +
    '<svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.7)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="16" height="16"><path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>' +
  '</div>' +
  /* Flèches prev/next fullscreen (groupées en bas centrées) */
  '<div class="fsNav">' +
    '<button class="fsArr l" id="fsArrL" type="button" aria-label="prev">‹</button>' +
    '<button class="fsArr r" id="fsArrR" type="button" aria-label="next">›</button>' +
  '</div>' +
  '<div class="fs-wrap" id="fsWrap">' +
    '<div class="vimg c1" id="fsImg" style="max-width:100%;max-height:100%;transition:transform .3s"></div>' +
  '</div>' +
'</div>' +

/* Sheet: Les prix */
'<div class="overlay" id="ov-rewards">' +
  '<div class="sheet">' +
    '<div class="sheet-handle"></div>' +
    '<div class="sheet-nav">' +
      '<div style="width:26px"></div>' +
      '<span class="sheet-nav-title">'+esc(CFG.lang.sheet_rewards_title)+'</span>' +
      '<div class="icon-btn" id="closeRewards" style="width:26px;height:26px">' +
        '<svg width="9" height="9" viewBox="0 0 10 10" fill="none" stroke="rgba(255,255,255,.45)" stroke-width="2" stroke-linecap="round"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg>' +
      '</div>' +
    '</div>' +
    '<div class="sheet-body">' +
      '<p class="info-eye">'+esc(CFG.lang.rewards_eyebrow)+'</p>' +
      '<p class="info-hl">'+CFG.lang.rewards_headline_html+'</p>' +
      bvBuildRewardTiersHTML('sheet') +
      '<p class="info-rules">'+esc((CFG.lang.rewards_rules || '').replace('{date}', rd))+'</p>' +
    '</div>' +
  '</div>' +
'</div>' +

/* Sheet: Le vote */
'<div class="overlay" id="ov-rules">' +
  '<div class="sheet">' +
    '<div class="sheet-handle"></div>' +
    '<div class="sheet-nav">' +
      '<div style="width:26px"></div>' +
      '<span class="sheet-nav-title">'+esc(CFG.lang.pill_about)+'</span>' +
      '<div class="icon-btn" id="closeRules" style="width:26px;height:26px">' +
        '<svg width="9" height="9" viewBox="0 0 10 10" fill="none" stroke="rgba(255,255,255,.45)" stroke-width="2" stroke-linecap="round"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg>' +
      '</div>' +
    '</div>' +
    '<div class="sheet-body">' +
      '<div class="tier" style="border-color:rgba(255,255,255,.14);background:rgba(255,255,255,.02);padding:14px;margin-bottom:0">' +
        '<div class="vote-rule"><span class="vote-rule-icon">🖼</span><div class="vote-rule-text"><h4>'+esc(CFG.lang.rule1_title)+'</h4><p>'+esc(CFG.lang.rule1_body)+'</p></div></div>' +
        '<div class="vote-rule"><span class="vote-rule-icon">📅</span><div class="vote-rule-text"><h4>'+esc(CFG.lang.rule2_title)+'</h4><p>'+esc(CFG.lang.rule2_body)+'</p></div></div>' +
        '<div class="vote-rule"><span class="vote-rule-icon">⭐</span><div class="vote-rule-text"><h4>'+esc(CFG.lang.rule3_title)+'</h4><p>'+esc(CFG.lang.rule3_body)+'</p></div></div>' +
        '<div class="vote-rule"><span class="vote-rule-icon">🎨</span><div class="vote-rule-text"><h4>'+esc(CFG.lang.rule4_title)+'</h4><p>'+esc(CFG.lang.rule4_body)+'</p></div></div>' +
        '<div class="vote-rule"><span class="vote-rule-icon">🔒</span><div class="vote-rule-text"><h4>'+esc(CFG.lang.rule5_title)+'</h4><p>'+esc(CFG.lang.rule5_body)+'</p></div></div>' +
      '</div>' +
    '</div>' +
  '</div>' +
'</div>' +

/* Modal Quitter le vote */
'<div class="bv-exit-modal" id="bisou-exit-modal" style="position:fixed;inset:0;z-index:2000;background:rgba(0,0,0,.8);backdrop-filter:blur(8px);display:none;align-items:center;justify-content:center">' +
  '<div style="background:#1c1c1c;border:1px solid rgba(255,255,255,.1);border-radius:12px;padding:24px;text-align:center;max-width:300px;margin:0 20px;box-shadow:0 16px 48px rgba(0,0,0,.6);animation:fadeUp .2s ease">' +
    '<p style="font-family:Georgia,serif;font-style:italic;font-size:calc(18px * var(--ts));margin-bottom:12px;color:#fff">' + esc(CFG.lang.exit_modal_title) + '</p>' +
    '<p style="font-size:calc(11px * var(--ts));color:rgba(255,255,255,.6);margin-bottom:24px;line-height:1.5">' + esc(CFG.lang.exit_modal_body) + '</p>' +
    '<div style="display:flex;gap:10px">' +
      '<button id="bisou-exit-stay" style="flex:1;padding:10px;border-radius:8px;border:1px solid rgba(255,255,255,.15);background:rgba(255,255,255,.05);color:#fff;font-family:\'Roboto Mono\',monospace;font-size:calc(9px * var(--ts));text-transform:uppercase;letter-spacing:.05em;cursor:pointer;transition:background .2s">' + esc(CFG.lang.exit_modal_stay) + '</button>' +
      '<button id="bisou-exit-confirm" style="flex:1;padding:10px;border-radius:8px;border:none;background:#fff;color:#111;font-family:\'Roboto Mono\',monospace;font-size:calc(9px * var(--ts));font-weight:600;text-transform:uppercase;letter-spacing:.05em;cursor:pointer;transition:transform .2s">' + esc(CFG.lang.exit_modal_leave) + '</button>' +
    '</div>' +
  '</div>' +
'</div>' +

/* Modal Changement de langue — avec drapeau courant → flèche → drapeau cible */
(function buildLangModal() {
  var path = window.location.pathname;
  var isEn = path.indexOf('/en/') === 0 || path === '/en';
  var curCode = isEn ? 'gb' : 'fr';
  var tgtCode = isEn ? 'fr' : 'gb';
  var curFlag = bvFlagSVG(curCode, 'bv-lang-modal-flag');
  var tgtFlag = bvFlagSVG(tgtCode, 'bv-lang-modal-flag');
  var tgtFlagBtn = bvFlagSVG(tgtCode, 'bv-lang-btn-flag');
  return '<div class="bv-lang-modal" id="bisou-lang-modal" style="position:fixed;inset:0;z-index:3000;background:rgba(0,0,0,.8);backdrop-filter:blur(8px);display:none;align-items:center;justify-content:center">' +
    '<div style="background:#1c1c1c;border:1px solid rgba(255,255,255,.1);border-radius:12px;padding:28px 24px;text-align:center;max-width:340px;margin:0 20px;box-shadow:0 16px 48px rgba(0,0,0,.6);animation:fadeUp .2s ease">' +
      '<div class="bv-lang-modal-title" style="display:flex;align-items:center;justify-content:center;gap:12px;margin:0 0 8px">' +
        curFlag +
        '<span class="bv-lang-modal-arrow" aria-hidden="true">→</span>' +
        tgtFlag +
      '</div>' +
      '<p style="font-family:Georgia,serif;font-style:italic;font-size:calc(16px * var(--ts));margin:0 0 10px;color:#fff">' + esc(CFG.lang.lang_confirm_title) + '</p>' +
      '<p style="font-size:calc(11px * var(--ts));color:rgba(255,255,255,.6);margin:0 0 24px;line-height:1.5">' + esc(CFG.lang.lang_confirm_body) + '</p>' +
      '<div style="display:flex;gap:10px">' +
        '<button id="bv-lang-cancel" type="button" style="flex:1;padding:11px 10px;border-radius:8px;border:1px solid rgba(255,255,255,.15);background:rgba(255,255,255,.05);color:#fff;font-family:\'Roboto Mono\',monospace;font-size:calc(9px * var(--ts));text-transform:uppercase;letter-spacing:.05em;cursor:pointer">' + esc(CFG.lang.lang_confirm_cancel) + '</button>' +
        '<button id="bv-lang-confirm" type="button" style="flex:1;padding:11px 10px;border-radius:8px;border:none;background:#fff;color:#111;font-family:\'Roboto Mono\',monospace;font-size:calc(9px * var(--ts));font-weight:600;text-transform:uppercase;letter-spacing:.05em;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:6px">' + esc(CFG.lang.lang_confirm_ok) + tgtFlagBtn + '</button>' +
      '</div>' +
    '</div>' +
  '</div>';
})();
}

/* ═══════════════════════════════════
   bindAllEvents — attach ALL events after innerHTML
═══════════════════════════════════ */
function bindAllEvents() {
  /* Gallery tabs */
  document.getElementById('tg').addEventListener('click', function(){ goTab('grid'); });
  document.getElementById('ts').addEventListener('click', function(){ goTab('sel'); });

  /* Gallery bar button */
  document.getElementById('gbtn').addEventListener('click', function(){ goTab('sel'); });

  /* Viewer close */
  document.getElementById('vCloseBtn').addEventListener('click', function(){ closeViewer(); });
  var cvBtn = document.getElementById('bvCloseViewer');
  if (cvBtn) cvBtn.addEventListener('click', function(){ closeViewer(); });

  /* Arrows */
  document.getElementById('aL').addEventListener('click', function(){ vNav(-1); });
  document.getElementById('aR').addEventListener('click', function(){ vNav(1); });

  /* Bouton "Ajouter à ma sélection" — ajoute/retire la photo courante du top 5 */
  var vAddEl = document.getElementById('vAddBtn');
  if (vAddEl) vAddEl.addEventListener('click', function(){
    var p = P[vCur]; if(!p) return;
    var idx = topVotes.indexOf(p.id);
    if (idx > -1) { topVotes[idx] = null; }
    else {
      var em = topVotes.indexOf(null);
      if (em === -1) { toast(CFG.lang.toast_top5_full); return; }
      topVotes[em] = p.id;
      if (navigator.vibrate) navigator.vibrate(10);
    }
    refresh();
  });

  /* Context toggle */
  document.getElementById('vctxBar').addEventListener('click', function(){ toggleCtx(); });

  /* Expand (rotate moved to fullscreen) */
  document.getElementById('vExpandBtn').addEventListener('click', function(){ enterFullscreen(); });

  /* Header action buttons in viewer */
  document.getElementById('hdrRewards').addEventListener('click', function(){ openSheet('rewards'); });
  document.getElementById('hdrRules').addEventListener('click', function(){ openSheet('rules'); });

  /* Fullscreen — with rotate button */
  var _fsRotated = false;
  var fsExitEl = document.getElementById('fsExit');
  var fsRotBtnEl = document.getElementById('fsRotBtn');
  var fsWrapEl = document.getElementById('fsWrap');

  if (fsExitEl) {
    fsExitEl.addEventListener('click', function(e) {
      e.stopPropagation();
      _fsRotated = false;
      var fi = document.getElementById('fsImg');
      if (fi) fi.style.transform = '';
      exitFullscreen();
    });
  }
  if (fsRotBtnEl) {
    fsRotBtnEl.addEventListener('click', function(e) {
      e.stopPropagation();
      _fsRotated = !_fsRotated;
      var fi = document.getElementById('fsImg');
      if (!fi) return;
      if (_fsRotated) {
        var r = fi.getBoundingClientRect();
        var vw = window.innerWidth, vh = window.innerHeight;
        /* Réserve 70px en bas (zone flèches prev/next) pour éviter que l'image tournée passe derrière elles */
        var usableVh = vh - 70;
        /* Marge différenciée : verticales tournées débordent facilement sur mobile haut → facteur plus prudent.
           Horizontales tournées laissent du vide à cause du ratio → facteur plus agressif. */
        var isVertical = fi.classList.contains('v');
        var marginFactor = isVertical ? 0.86 : 0.98;
        var scale = Math.min(vw / r.height, usableVh / r.width) * marginFactor;
        fi.style.transform = 'rotate(90deg) scale(' + scale + ')';
      } else {
        fi.style.transform = '';
      }
    });
  }
  if (fsWrapEl) {
    fsWrapEl.addEventListener('click', function(e) {
      /* Ignore clics sur les flèches prev/next */
      if (e.target.closest && e.target.closest('.fsArr')) return;
      exitFullscreen();
    });
  }

  /* Flèches prev/next fullscreen */
  var fsArrL = document.getElementById('fsArrL');
  var fsArrR = document.getElementById('fsArrR');
  if (fsArrL) fsArrL.addEventListener('click', function(e) { e.stopPropagation(); vNav(-1); });
  if (fsArrR) fsArrR.addEventListener('click', function(e) { e.stopPropagation(); vNav(1); });

  /* Swipe horizontal fullscreen → prev/next (touch-action géré via CSS) */
  var fsTouchStart = null;
  var fsEl = document.getElementById('sfs');
  if (fsEl) {
    fsEl.addEventListener('touchstart', function(e) {
      if (e.touches.length !== 1) { fsTouchStart = null; return; }
      fsTouchStart = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now() };
    }, { passive: true });
    fsEl.addEventListener('touchend', function(e) {
      if (!fsTouchStart || e.changedTouches.length !== 1) return;
      var dx = e.changedTouches[0].clientX - fsTouchStart.x;
      var dy = e.changedTouches[0].clientY - fsTouchStart.y;
      var dt = Date.now() - fsTouchStart.t;
      fsTouchStart = null;
      if (dt > 500) return;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        if (dx < 0) vNav(1); else vNav(-1);
      }
    }, { passive: true });
  }

  /* Selection tabs */
  document.getElementById('ssTabGrid').addEventListener('click', function(){ goTab('grid'); });
  document.getElementById('ssTabSel').addEventListener('click', function(){ goTab('sel'); });

  /* View toggle (desktop) — both nav bars share IDs vtGrid1/vtDetail1 (gallery) and vtGrid2/vtDetail2 (viewer) */
  ['1', '2'].forEach(function(id) {
    var btnGrid = document.getElementById('vtGrid' + id);
    var btnDet = document.getElementById('vtDetail' + id);
    if (btnGrid) btnGrid.addEventListener('click', function(){ closeViewer(); });
    if (btnDet) btnDet.addEventListener('click', function(){ if(!document.getElementById('sv').classList.contains('on')) openViewer(vCur, 'vote'); });
  });

  /* Selection pills */
  document.getElementById('giftPill').addEventListener('click', function(){ openSheet('rewards'); });
  document.getElementById('infoPill').addEventListener('click', function(){ openSheet('rules'); });

  /* Sheet close */
  document.getElementById('closeRewards').addEventListener('click', function(){ closeSheet('rewards'); });
  document.getElementById('closeRules').addEventListener('click', function(){ closeSheet('rules'); });

  /* Overlay click-outside : ferme la modale quand on clique hors du .sheet */
  document.querySelectorAll('.overlay').forEach(function(ov){
    ov.addEventListener('click', function(e){
      var sheet = ov.querySelector('.sheet');
      if (sheet && !sheet.contains(e.target)) ov.classList.remove('on');
    });
  });

  /* Pill back */
  document.getElementById('pillBackBtn').addEventListener('click', function(){ closeViewer(); });

  /* Pill check — close viewer + go to Ma sélection when top 5 complete */
  document.getElementById('pillCheck').addEventListener('click', function(){ closeViewer(); goTab('sel'); });

  /* Selection validate */
  document.getElementById('svbtn').addEventListener('click', function(){ handleValidate(); });

  /* Preview */
  document.getElementById('visBtn').addEventListener('click', function(){ openPreview(); });

  /* Keyboard */
  document.addEventListener('keydown', function(e){
    if(!document.getElementById('sv').classList.contains('on'))return;
    if(e.key==='ArrowRight')vNav(1);
    if(e.key==='ArrowLeft') vNav(-1);
    if(e.key==='Escape'){
      if(document.getElementById('sfs').classList.contains('on'))exitFullscreen();
      else closeViewer();
    }
  });

  /* Wheel anywhere in viewer — desktop only (mobile doesn't fire wheel and passive:false hurts scroll perf) */
  if (isDesktop()) {
    var sv=document.getElementById('sv');
    var _lastWheel = 0;
    if (sv) {
      sv.addEventListener('wheel', function(e) {
        if (e.target.closest('.vctx-body')) return;
        e.preventDefault();
        var now = Date.now();
        if (now - _lastWheel < 350) return;
        if (Math.abs(e.deltaY) < 20) return;
        _lastWheel = now;
        if (e.deltaY > 0) vNav(1); else vNav(-1);
      }, { passive: false });
    }
  }

  /* Window resize */
  window.addEventListener('resize', function(){
    applyLayout();
    if(document.getElementById('sv').classList.contains('on')) requestAnimationFrame(updateImgSize);
  });

  /* Modal Actions */
  var exitModal = document.getElementById('bisou-exit-modal');
  var exitStay = document.getElementById('bisou-exit-stay');
  var exitConfirm = document.getElementById('bisou-exit-confirm');

  if (exitStay) exitStay.addEventListener('click', function(){ if(exitModal) exitModal.style.display='none'; });
  if (exitConfirm) exitConfirm.addEventListener('click', function(){ window.location.href = bvRootUrl(); });

  /* Afficher la modale sur TOUS les boutons de sortie */
  var allExitBtns = root.querySelectorAll('.bv-exit-btn, #exitIconSel');
  allExitBtns.forEach(function(btn) {
    btn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      if (exitModal) exitModal.style.display = 'flex';
    });
  });

  /* Fermer la modale si on clique à l'extérieur de la boîte */
  if (exitModal) {
    exitModal.addEventListener('click', function(e) {
      if (e.target === exitModal) exitModal.style.display = 'none';
    });
  }

  /* ─── Modale Changement de langue ─── */
  var langModal = document.getElementById('bisou-lang-modal');
  var langCancel = document.getElementById('bv-lang-cancel');
  var langConfirm = document.getElementById('bv-lang-confirm');
  var _pendingLangUrl = null;
  var langToggles = root.querySelectorAll('[data-bv-lang-toggle]');
  langToggles.forEach(function (a) {
    a.addEventListener('click', function (e) {
      e.preventDefault();
      _pendingLangUrl = a.getAttribute('href');
      if (langModal) langModal.style.display = 'flex';
    });
  });
  if (langCancel) langCancel.addEventListener('click', function () {
    if (langModal) langModal.style.display = 'none';
    _pendingLangUrl = null;
  });
  if (langConfirm) langConfirm.addEventListener('click', function () {
    if (_pendingLangUrl) window.location.href = _pendingLangUrl;
  });
  /* Click-outside ferme la modale de langue (fond sombre) */
  if (langModal) {
    langModal.addEventListener('click', function (e) {
      if (e.target === langModal) {
        langModal.style.display = 'none';
        _pendingLangUrl = null;
      }
    });
  }

  /* Desktop: close button removed — "Toutes les images" button handles this */

  /* Desktop: drag image from viewer to selection panel */
  if (isDesktop()) {
    var vppEl = document.getElementById('vpp');
    var ssEl = document.getElementById('ss');
    if (vppEl && ssEl) {
      vppEl.setAttribute('draggable', 'true');
      vppEl.addEventListener('dragstart', function(e) {
        var p = P[vCur];
        if (!p) return;
        e.dataTransfer.effectAllowed = 'copy';
        e.dataTransfer.setData('text/plain', String(p.id));
        e.dataTransfer.setData(DT_UNRANKED, String(p.id));
        /* Ghost miniature — setTimeout(0) pour compatibilité Safari */
        var ghost = document.createElement('div');
        ghost.style.cssText = 'width:56px;height:56px;border-radius:8px;overflow:hidden;position:fixed;top:-200px;left:-200px;z-index:-1;box-shadow:0 4px 16px rgba(0,0,0,.5)';
        var gImg = document.createElement('img');
        gImg.src = p.urlSm || p.url;
        gImg.style.cssText = 'width:100%;height:100%;object-fit:cover';
        ghost.appendChild(gImg);
        document.body.appendChild(ghost);
        e.dataTransfer.setDragImage(ghost, 28, 28);
        setTimeout(function() { document.body.removeChild(ghost); }, 0);
        ssEl.classList.add('drag-highlight');
      });
      vppEl.addEventListener('dragend', function() {
        ssEl.classList.remove('drag-highlight');
      });
      /* Allow drop on the entire selection panel */
      ssEl.addEventListener('dragover', function(e) { e.preventDefault(); });
      ssEl.addEventListener('drop', function(e) {
        e.preventDefault();
        ssEl.classList.remove('drag-highlight');
        var id = getDroppedId(e);
        if (!id || topVotes.includes(id)) return;
        var empty = topVotes.indexOf(null);
        if (empty === -1) { toast(CFG.lang.toast_top5_full); return; }
        topVotes[empty] = id;
        refresh();
      });
    }
  }
}

/* ═══════════════════════════════════
   Screen builders — maquette haut de gamme
═══════════════════════════════════ */

var bvCountdownInt = null;
function bvClearCountdown() {
  if (bvCountdownInt) { clearInterval(bvCountdownInt); bvCountdownInt = null; }
}

function bvWrap(content) {
  if (root) root.classList.add('bv-mode-screens');
  document.body.classList.remove('bv-engine-active');
  return '<div class="bv-screen-host">' +
           '<div class="bv-screen-bg">' +
             '<div class="bv-blob bv-blob-1"></div>' +
             '<div class="bv-blob bv-blob-2"></div>' +
             '<div class="bv-blob bv-blob-3"></div>' +
             '<div class="bv-blob bv-blob-4"></div>' +
           '</div>' +
           '<div class="bv-screen">' + content + '</div>' +
         '</div>';
}

/* Top 5 visuel : 5 slots avec miniatures + médailles sur le top 3 */
function bvBuildTop5HTML(rankings) {
  var medals = { 1: '🥇', 2: '🥈', 3: '🥉' };
  var html = '<div class="bv-top5">';
  for (var i = 1; i <= 5; i++) {
    var pid = rankings ? rankings[i] : null;
    var p = pid ? photo(pid) : null;
    html += '<div class="bv-top5-slot">';
    if (p && (p.urlSm || p.url)) {
      html += '<img src="' + esc(p.urlSm || p.url) + '" alt="' + esc(p.t || '') + '">';
    }
    if (medals[i]) {
      html += '<span class="bv-top5-medal">' + medals[i] + '</span>';
    } else {
      html += '<span class="bv-top5-rank">#' + i + '</span>';
    }
    html += '</div>';
  }
  html += '</div>';
  return html;
}

/* Source unique des tiers de récompenses (évite duplication sheet/flip_back) */
function bvGetRewardTiers() {
  var L = CFG.lang;
  return [
    {
      icon:  '🏆',
      title: L.flip_back_tier1_title || L.rewards_tier1_lbl_b || '',
      prize: L.rewards_tier1_title || '',
      sub:   L.rewards_tier1_val || '',
      lblA:  L.rewards_tier1_lbl_a || '',
      lblB:  L.rewards_tier1_lbl_b || '',
      alts:  [],
      cls:   'g1'
    },
    {
      icon:  '🥇',
      title: L.flip_back_tier2_title || L.rewards_tier2_lbl_b || '',
      prize: L.rewards_tier2_title || '',
      sub:   L.rewards_tier2_val || '',
      lblA:  L.rewards_tier2_lbl_a || '',
      lblB:  L.rewards_tier2_lbl_b || '',
      cls:   'g2'
    },
    {
      icon:  '🥈',
      title: L.flip_back_tier3_title || L.rewards_tier3_lbl_b || '',
      prize: L.rewards_tier3_title || '',
      sub:   L.rewards_tier3_val || '',
      lblA:  L.rewards_tier3_lbl_a || '',
      lblB:  L.rewards_tier3_lbl_b || '',
      cls:   'g3'
    }
  ];
}

/* Helper unifié — context: 'flip_back' | 'sheet' */
function bvBuildRewardTiersHTML(context) {
  var tiers = bvGetRewardTiers();
  if (context === 'sheet') {
    return tiers.map(function(t) {
      var altsHtml = (t.alts || []).map(function(v) {
        return '<span class="tier-or">' + esc(t.or) + '</span><p class="tier-val">' + esc(v) + '</p>';
      }).join('');
      var medals = { g1: '🥇', g2: '🥈', g3: '🥉' };
      return '<div class="tier ' + t.cls + '">' +
        '<div class="tier-lbl"><span style="color:var(--r' + t.cls.slice(-1) + ')">' + medals[t.cls] + '</span>' +
          '<span style="color:var(--r' + t.cls.slice(-1) + ')">' + esc(t.lblA) + '</span>' +
          '<span style="color:var(--r' + t.cls.slice(-1) + ');font-weight:500">' + esc(t.lblB) + '</span></div>' +
        '<p class="tier-title">' + esc(t.prize) + '</p>' +
        '<p class="tier-val">' + esc(t.sub) + '</p>' +
        altsHtml +
      '</div>';
    }).join('');
  }
  /* flip_back par défaut (format compact centré) */
  return tiers.map(function(t) {
    return '<div class="bv-pb-tier">' +
      '<div class="bv-pb-tier-icon">' + t.icon + '</div>' +
      '<div class="bv-pb-tier-title">' + esc(t.title) + '</div>' +
      '<div class="bv-pb-tier-prize">' + esc(t.prize) + '</div>' +
      '<div class="bv-pb-tier-sub">' + esc(t.sub) + '</div>' +
    '</div>';
  }).join('');
}

/* Verso de la flip card : récompenses (centré) */
function bvBuildFlipBackHTML() {
  var fine = (CFG.lang.flip_back_fine_html || '').replace('{date}', esc(CFG.resultsDate));
  return '<div class="bv-flip-back">' +
    '<button class="bv-close-flip" data-bv-flip-toggle aria-label="' + esc(CFG.lang.aria_close) + '">✕</button>' +
    '<div class="bv-pb-eyebrow">' + esc(CFG.lang.rewards_eyebrow) + '</div>' +
    '<p class="bv-pb-tagline">' + esc(CFG.lang.flip_back_tagline) + '</p>' +
    '<p class="bv-pb-intro">' + esc(CFG.lang.ranking_desc) + '</p>' +
    bvBuildRewardTiersHTML('flip_back') +
    '<div class="bv-pb-fine">' + fine + '</div>' +
  '</div>';
}

/* Wire les triggers de flip (prize teaser + voir plus + close) */
function bvWireFlip() {
  var inner = root.querySelector('#bv-flip');
  if (!inner) return;
  var triggers = root.querySelectorAll('[data-bv-flip-toggle]');
  triggers.forEach(function(t) {
    t.addEventListener('click', function(e) {
      e.preventDefault();
      inner.classList.toggle('flipped');
    });
  });
}

/* Sparkles confetti pour l'écran de validation */
function bvLaunchSparkles() {
  var container = root.querySelector('#bv-sparkle');
  if (!container) return;
  container.innerHTML = '';
  var angles = [0, 45, 90, 135, 180, 225, 270, 315, 350, 22];
  var cols = ['#EE3D29', '#F58567', '#D9D6C7', '#235071', '#E5C1CD'];
  angles.forEach(function(a, i) {
    var sp = document.createElement('div');
    sp.className = 'bv-sp';
    var rad = a * Math.PI / 180;
    var dist = 28 + Math.random() * 16;
    var tx = Math.cos(rad) * dist;
    var ty = Math.sin(rad) * dist;
    sp.style.setProperty('--bv-tx', 'translate(' + tx + 'px,' + ty + 'px)');
    sp.style.background = cols[i % cols.length];
    sp.style.animationDelay = (i * 0.06) + 's';
    sp.style.animationDuration = (0.9 + Math.random() * 0.4) + 's';
    container.appendChild(sp);
  });
}

/* Compte à rebours ; masque la ligne si vide/passée.
   `cible` permet de viser une ouverture au lieu d une cloture. Sans argument,
   le comportement d origine est conserve : on vise CFG.voteDeadline, qui suit
   desormais closes_at de la base. */
function bvStartCountdown(cible) {
  bvClearCountdown();
  var line = root.querySelector('.bv-date-line');
  var vise = cible || CFG.voteDeadline;
  if (!vise) { if (line) line.style.display = 'none'; return; }
  var deadline = new Date(vise);
  if (isNaN(deadline.getTime()) || deadline.getTime() < Date.now()) {
    if (line) line.style.display = 'none';
    return;
  }
  var dEl = root.querySelector('#bv-cd-d');
  var hEl = root.querySelector('#bv-cd-h');
  var mEl = root.querySelector('#bv-cd-m');
  var sEl = root.querySelector('#bv-cd-s');
  if (!dEl || !hEl || !mEl || !sEl) return;
  function pad(n) { var s = String(n); return s.length < 2 ? '0' + s : s; }
  function tick() {
    var diff = Math.max(0, deadline.getTime() - Date.now());
    var d = Math.floor(diff / 86400000); diff %= 86400000;
    var h = Math.floor(diff / 3600000);  diff %= 3600000;
    var m = Math.floor(diff / 60000);    diff %= 60000;
    var s = Math.floor(diff / 1000);
    dEl.textContent = pad(d);
    hEl.textContent = pad(h);
    mEl.textContent = pad(m);
    sEl.textContent = pad(s);
  }
  tick();
  bvCountdownInt = setInterval(tick, 1000);
}

function bvDeadlinePassed() {
  if (!CFG.voteDeadline) return false;
  var deadline = new Date(CFG.voteDeadline);
  return !isNaN(deadline.getTime()) && deadline.getTime() < Date.now();
}

/* ─── S1 / S2 : Vote fermé ─── */
function showClosed() {
  bvClearCountdown();
  var displayName = CFG.name || '';
  var content;

  if (CFG.isLogged) {
    /* S2 — connecté : remerciement personnalisé */
    var hello = displayName
      ? (CFG.lang.s2_hello_html || '').replace('{name}', esc(displayName))
      : CFG.lang.s2_hello_anon;
    content =
      '<div class="bv-card">' +
        '<div class="bv-eyebrow"><span class="bv-dot closed"></span>' + esc(CFG.lang.eyebrow_closed) + '</div>' +
        '<h1>' + hello + '</h1>' +
        '<p>' + esc(CFG.lang.s2_body) + '</p>' +
        '<div class="bv-btn-group">' +
          '<a class="bv-btn bv-btn-primary" href="' + bvRootUrl() + '">' + esc(CFG.lang.btn_see_prints) + '</a>' +
        '</div>' +
      '</div>';
  } else {
    /* S1 — déconnecté : flip avec récompenses au verso */
    content =
      '<div class="bv-flip-wrap"><div class="bv-flip-inner" id="bv-flip">' +
        '<div class="bv-flip-front"><div class="bv-card">' +
          '<div class="bv-eyebrow"><span class="bv-dot closed"></span>' + esc(CFG.lang.eyebrow_closed) + '</div>' +
          '<h1>' + esc(CFG.lang.s1_title) + '</h1>' +
          '<p>' + esc(CFG.lang.s1_body) + '</p>' +
          '<button class="bv-info-pill" data-bv-flip-toggle type="button">' +
            '<span class="bv-info-pill-emoji" aria-hidden="true">🎁</span>' +
            '<span>' + esc(CFG.lang.s1_prize_teaser) + '</span>' +
          '</button>' +
          '<div class="bv-btn-group">' +
            '<a class="bv-btn bv-btn-primary" href="' + esc(CFG.loginUrl) + '">' + esc(CFG.lang.btn_login_register) + '</a>' +
            '<a class="bv-btn-ghost" href="' + bvRootUrl() + '">' + esc(CFG.lang.btn_back_shop) + '</a>' +
          '</div>' +
        '</div></div>' +
        bvBuildFlipBackHTML() +
      '</div></div>';
  }

  root.innerHTML = bvWrap(content);
  bvWireFlip();
}

/* ─── S3 : Vote ouvert + Non connecté ─── */
function showAuth() {
  bvClearCountdown();
  var content =
    '<div class="bv-flip-wrap"><div class="bv-flip-inner" id="bv-flip">' +
      '<div class="bv-flip-front"><div class="bv-card">' +
        '<div class="bv-eyebrow"><span class="bv-dot open"></span>' + esc(CFG.lang.eyebrow_open) + '</div>' +
        '<h1>' + esc(CFG.lang.s3_title) + '</h1>' +
        '<p>' + CFG.lang.s3_body_html + '</p>' +
        '<button class="bv-info-pill" data-bv-flip-toggle type="button">' +
          '<span class="bv-info-pill-emoji" aria-hidden="true">🎁</span>' +
          '<span>' + esc(CFG.lang.learn_more) + '</span>' +
        '</button>' +
        '<div class="bv-timer-row">' +
          '<span class="bv-t-icon">⏱</span>' +
          '<span class="bv-t-text">' + esc(CFG.lang.timer_about_5_min) + '</span>' +
        '</div>' +
        '<div class="bv-btn-group">' +
          '<a class="bv-btn bv-btn-primary" href="' + esc(CFG.loginUrl) + '">' + esc(CFG.lang.btn_login_register) + '</a>' +
          '<a class="bv-btn-ghost" href="' + bvRootUrl() + '">' + esc(CFG.lang.btn_back_shop) + '</a>' +
        '</div>' +
      '</div></div>' +
      bvBuildFlipBackHTML() +
    '</div></div>';

  root.innerHTML = bvWrap(content);
  bvWireFlip();
}

/* ─── S4 : Sas d'accueil avant le vote ─── */
function showWelcome() {
  bvClearCountdown();
  var displayName = CFG.name || '';
  var greet = displayName
    ? (CFG.lang.s4_greet_html || '').replace('{name}', esc(displayName))
    : CFG.lang.s4_greet_anon;
  var content =
    '<div class="bv-flip-wrap"><div class="bv-flip-inner" id="bv-flip">' +
      '<div class="bv-flip-front"><div class="bv-card">' +
        '<div class="bv-eyebrow"><span class="bv-dot open"></span>' + esc(CFG.lang.eyebrow_open) + '</div>' +
        '<h1>' + greet + '</h1>' +
        '<p>' + esc(CFG.lang.s4_body) + '</p>' +
        '<button class="bv-info-pill" data-bv-flip-toggle type="button">' +
          '<span class="bv-info-pill-emoji" aria-hidden="true">🎁</span>' +
          '<span>' + esc(CFG.lang.learn_more) + '</span>' +
        '</button>' +
        '<div class="bv-timer-row small">' +
          '<span class="bv-t-icon">⏱</span>' +
          '<span class="bv-t-text">' + esc(CFG.lang.timer_about_5_min) + '</span>' +
        '</div>' +
        '<div class="bv-btn-group">' +
          '<button class="bv-btn bv-btn-primary" id="bv-start-btn" type="button">' + esc(CFG.lang.btn_start) + '</button>' +
          '<a class="bv-btn-ghost" href="' + bvRootUrl() + '">' + esc(CFG.lang.btn_back_shop) + '</a>' +
        '</div>' +
      '</div></div>' +
      bvBuildFlipBackHTML() +
    '</div></div>';

  root.innerHTML = bvWrap(content);
  var btn = root.querySelector('#bv-start-btn');
  if (btn) btn.addEventListener('click', startVote);
  bvWireFlip();
}

/* ─── S5 : Vote validé ─── */
function showConfirmation(rankings) {
  bvClearCountdown();
  var displayName = CFG.name || '';
  var thanks = displayName
    ? (CFG.lang.s5_thanks_html || '').replace('{name}', esc(displayName))
    : CFG.lang.s5_thanks_anon;
  var body = (CFG.lang.s5_body_html || '').replace('{date}', esc(CFG.resultsDate));
  var content =
    '<div class="bv-card">' +
      '<div class="bv-eyebrow"><span class="bv-dot done"></span>' + esc(CFG.lang.eyebrow_validated) + '</div>' +
      '<div class="bv-check-wrap">' +
        '<div class="bv-check">✓</div>' +
        '<div class="bv-sparkle" id="bv-sparkle"></div>' +
      '</div>' +
      '<h1>' + esc(CFG.lang.s5_title) + '</h1>' +
      '<p>' + thanks + ' ' + body + '</p>' +
      bvBuildTop5HTML(rankings) +
      '<div class="bv-btn-group">' +
        '<a class="bv-btn bv-btn-primary" href="' + bvRootUrl() + '">' + esc(CFG.lang.btn_see_prints_arrow) + '</a>' +
        '<a class="bv-btn-arrow" href="' + esc(bvLatestCollectionUrl()) + '">' + esc(CFG.lang.btn_see_latest_arrow) + '</a>' +
      '</div>' +
    '</div>';

  root.innerHTML = bvWrap(content);
  bvLaunchSparkles();
}

/* ─── S6 : Déjà voté ─── */
function showAlreadyVoted(rankings) {
  bvClearCountdown();
  var displayName = CFG.name || '';
  var thanks = displayName
    ? (CFG.lang.s6_thanks_named || '').replace('{name}', esc(displayName))
    : CFG.lang.s6_thanks_anon;
  var body = (CFG.lang.s6_body_html || '').replace('{date}', esc(CFG.resultsDate));
  var content =
    '<div class="bv-card">' +
      '<div class="bv-eyebrow"><span class="bv-dot done"></span>' + esc(CFG.lang.eyebrow_already_voted) + '</div>' +
      '<h1>' + esc(CFG.lang.s6_title) + '</h1>' +
      '<p>' + thanks + body + '</p>' +
      bvBuildTop5HTML(rankings) +
      '<div class="bv-date-line">' +
        '<span class="bv-dl-label">' + esc(CFG.lang.countdown_results) + '</span>' +
        '<span class="bv-dl-count"><span id="bv-cd-d">--</span>' + esc(CFG.lang.countdown_unit_d) + '&nbsp;&nbsp;<span id="bv-cd-h">--</span>' + esc(CFG.lang.countdown_unit_h) + '&nbsp;&nbsp;<span id="bv-cd-m">--</span>' + esc(CFG.lang.countdown_unit_m) + '&nbsp;&nbsp;<span id="bv-cd-s">--</span>' + esc(CFG.lang.countdown_unit_s) + '</span>' +
      '</div>' +
      '<div class="bv-btn-group">' +
        '<a class="bv-btn bv-btn-primary" href="' + bvRootUrl() + '">' + esc(CFG.lang.btn_see_prints_arrow) + '</a>' +
        '<a class="bv-btn-arrow" href="' + esc(bvLatestCollectionUrl()) + '">' + esc(CFG.lang.btn_see_latest_arrow) + '</a>' +
      '</div>' +
    '</div>';

  root.innerHTML = bvWrap(content);
  bvStartCountdown();
}

/* ─── Erreur générique (fallback simple) ─── */
function showError() {
  bvClearCountdown();
  var content =
    '<div class="bv-card">' +
      '<div class="bv-eyebrow"><span class="bv-dot closed"></span>' + esc(CFG.lang.eyebrow_error) + '</div>' +
      '<h1>' + esc(CFG.lang.error_title) + '</h1>' +
      '<p>' + esc(CFG.lang.error_body) + '</p>' +
      '<div class="bv-btn-group">' +
        '<a class="bv-btn bv-btn-primary" href="javascript:location.reload()">' + esc(CFG.lang.btn_retry) + '</a>' +
        '<a class="bv-btn-ghost" href="' + bvRootUrl() + '">' + esc(CFG.lang.btn_back_shop) + '</a>' +
      '</div>' +
    '</div>';
  root.innerHTML = bvWrap(content);
}

/* ─── S7 : l app n a pas repondu ───
   ECRAN QUI REMPLACE UN MENSONGE. Quand /api/vote/config ne repond pas, la
   page ne sait pas si le scrutin est ouvert. Avant, elle interrogeait quand
   meme l identifiant du theme — la saison 7, close — et affichait « le vote
   est ferme » sous les dix photos de la saison precedente, alors que le
   scrutin de la saison 8 pouvait etre grand ouvert. Un visiteur repartait
   convaincu d avoir rate le vote.

   On prefere dire qu on ne sait pas. « Momentanement indisponible » est
   verifiable, reessayable, et n eteint pas une campagne en cours. */
function showIndisponible() {
  bvClearCountdown();
  var en = bvLocalePrefix() === '/en';
  var content =
    '<div class="bv-card">' +
      '<div class="bv-eyebrow"><span class="bv-dot closed"></span>' +
        esc(en ? 'Temporarily unavailable' : 'Momentanement indisponible') + '</div>' +
      '<h1>' + esc(en ? 'The vote cannot be reached' : 'Le vote est momentanement indisponible') + '</h1>' +
      '<p>' + esc(en
        ? 'We cannot reach the voting service right now. This does not mean the vote is closed. Please try again in a moment.'
        : 'Nous n arrivons pas a joindre le service de vote pour le moment. Cela ne veut pas dire que le vote est ferme. Reessayez dans un instant.') + '</p>' +
      '<div class="bv-btn-group">' +
        '<a class="bv-btn bv-btn-primary" href="javascript:location.reload()">' + esc(CFG.lang.btn_retry) + '</a>' +
        '<a class="bv-btn-ghost" href="' + bvRootUrl() + '">' + esc(CFG.lang.btn_back_shop) + '</a>' +
      '</div>' +
    '</div>';
  root.innerHTML = bvWrap(content);
}

/* ─── S8 : campagne ouverte plus tard ───
   L app sert la campagne des qu elle est active, mais retient les oeuvres
   jusqu a l heure d ouverture. Entre les deux, la page annoncait « vote
   ferme ». Elle annonce desormais l heure, avec le meme compte a rebours que
   l ecran des resultats. */
function showAvantOuverture() {
  bvClearCountdown();
  var en = bvLocalePrefix() === '/en';
  var quand = CFG.opensAt ? bvDateLisible(new Date(CFG.opensAt)) : '';
  var content =
    '<div class="bv-card">' +
      '<div class="bv-eyebrow"><span class="bv-dot closed"></span>' +
        esc(en ? 'Opening soon' : 'Ouverture prochaine') + '</div>' +
      '<h1>' + esc(en ? 'The vote opens on ' + quand : 'Le vote ouvre le ' + quand) + '</h1>' +
      '<p>' + esc(en
        ? 'The next selection is being prepared. Come back when the countdown reaches zero to pick your five favourites.'
        : 'La prochaine selection se prepare. Revenez quand le compte a rebours atteint zero pour choisir vos cinq preferees.') + '</p>' +
      '<div class="bv-date-line">' +
        '<span class="bv-dl-label">' + esc(en ? 'Opens in' : 'Ouverture dans') + '</span>' +
        '<span class="bv-dl-count"><span id="bv-cd-d">--</span>' + esc(CFG.lang.countdown_unit_d) + '&nbsp;&nbsp;<span id="bv-cd-h">--</span>' + esc(CFG.lang.countdown_unit_h) + '&nbsp;&nbsp;<span id="bv-cd-m">--</span>' + esc(CFG.lang.countdown_unit_m) + '&nbsp;&nbsp;<span id="bv-cd-s">--</span>' + esc(CFG.lang.countdown_unit_s) + '</span>' +
      '</div>' +
      '<div class="bv-btn-group">' +
        '<a class="bv-btn bv-btn-primary" href="' + bvRootUrl() + '">' + esc(CFG.lang.btn_see_prints) + '</a>' +
      '</div>' +
    '</div>';
  root.innerHTML = bvWrap(content);
  bvStartCountdown(CFG.opensAt);
}

/* ─── S9 : un lien de repetition qui ne mene plus nulle part ───
   « quand je vais dessus ça me dit "Le prochain vote sera bientot disponible !" »
   (Max, 18/08). Cet ecran-la est vrai pour un visiteur ordinaire et FAUX pour
   quelqu un qui tient un lien prive : il lui fait croire a une panne du site,
   alors que son jeton est simplement invalide, revoque, ou pointe sur une
   repetition close.

   On ne dit PAS laquelle des cinq causes s applique : cette page est publique,
   et le detail en ferait un oracle a deviner des jetons. Le motif exact se lit
   dans le cockpit, sur la vue Preparer, en collant le lien. */
function showLienRepetitionInvalide() {
  bvClearCountdown();
  var en = bvLocalePrefix() === '/en';
  var content =
    '<div class="bv-card">' +
      '<div class="bv-eyebrow"><span class="bv-dot closed"></span>' +
        esc(en ? 'Rehearsal link' : 'Lien de repetition') + '</div>' +
      '<h1>' + esc(en
        ? 'This rehearsal link is no longer valid'
        : 'Ce lien de repetition n est plus valide') + '</h1>' +
      '<p>' + esc(en
        ? 'The rehearsal it points to has been closed, or the link has been revoked. This is not a fault on the site: the public vote is unaffected.'
        : 'La repetition qu il designe a ete fermee, ou le lien a ete revoque. Ce n est pas une panne du site : le vote public, lui, n est pas concerne.') + '</p>' +
      '<div class="bv-btn-group">' +
        '<a class="bv-btn bv-btn-primary" href="' + bvRootUrl() + '">' + esc(CFG.lang.btn_back_shop) + '</a>' +
      '</div>' +
    '</div>';
  root.innerHTML = bvWrap(content);
}

/* ═══════════════════════════════════
   INIT
═══════════════════════════════════ */
function init() {
  root = document.getElementById('bisou-vote-root');
  if (!root) return;

  /* L'état du scrutin fait foi côté serveur, avant les anciens réglages
     `vote_open` / `vote_deadline` et avant l'authentification Shopify.
     Le classement déjà envoyé n'est plus renvoyé au navigateur : tant que
     l'identité du votant est déclarée et non prouvée, demander l'état de
     quelqu'un ne doit pas donner accès à son bulletin. */
  if (API) {
    apiConfig()
      .then(function(cfg){
        var etat = applyServerConfig(cfg);

        /* L app n a rien dit : on ne prononce PAS la fermeture du scrutin a
           partir d un identifiant de campagne perime lu dans le theme. */
        if (etat === 'injoignable') { showIndisponible(); return null; }

        /* UN LIEN PRIVE QUI NE MENE NULLE PART N EST PAS « le vote est ferme ».
           Quand l adresse porte `?apercu=` et que l app ne rend aucune campagne,
           la fermeture n est pas la bonne reponse : elle laisse croire a une
           panne. On nomme la vraie situation. */
        if ((etat === 'aucune' || etat === 'refusee') && apercuDuLien()) {
          console.warn('[bisou-vote] Lien de repetition sans campagne :', cfg && cfg.reason);
          showLienRepetitionInvalide();
          return null;
        }

        /* Le serveur est formel : rien n est ouvert. La fermeture est vraie. */
        if (etat === 'aucune')  { showClosed(); return null; }
        if (etat === 'refusee') { showClosed(); return null; }

        /* Campagne active mais heure d ouverture pas encore atteinte.
           Le drapeau `open` du serveur prime : on n annonce une ouverture a
           venir que s il confirme que le scrutin n est PAS encore ouvert. */
        if (CFG.campaignOpen !== true && bvOuvertureFuture()) { showAvantOuverture(); return null; }

        return apiStatus(CFG.customerId, CFG.campaignId);
      })
      .then(function(state){
        if (state === null) return;                  /* ecran deja rendu */
        if (!state || state.ok === false)            { showError(); return; }

        /* `closes_at` du statut est la valeur la plus fraiche des deux
           routes : elle prime sur celle de /config, lue une seconde plus tot. */
        if (state.closes_at || state.opens_at) bvAdopterDates(state.closes_at, state.opens_at);
        if (state.open === false && bvOuvertureFuture() && !CFG.bypassAuth) { showAvantOuverture(); return; }

        if (state.open === false && !CFG.bypassAuth) { showClosed(); return; }
        if (!CFG.isLogged && !CFG.bypassAuth)        { showAuth(); return; }
        if (hasVotedMark())                          { showAlreadyVoted(null); return; }
        if (state.has_voted)                         { showAlreadyVoted(null); return; }
        showWelcome();
      })
      .catch(function(err){
        /* Une panne de statut est, elle aussi, une ignorance : meme regle. */
        console.error('Vote status error:', err);
        showIndisponible();
      });
  } else {
    /* SANS ADRESSE D APP, LA PAGE NE PEUT RIEN DEMANDER.
       Le reglage « Adresse de l app vote » vide envoyait droit sur « une
       erreur est survenue », sans dire laquelle — constate en ligne le
       14/08/2026, sur le theme publie, pour tous les visiteurs. */
    console.error('[bisou-vote] Le reglage « Adresse de l app vote » est vide dans l editeur de theme. Renseignez https://artists.bisourivage.fr — sans lui, la page ne peut ni lire l etat du scrutin ni enregistrer un bulletin.');
    showError();
  }
}
function startVote() {
  /* Sortie du mode "screens" : on restaure le fond sombre du moteur de vote
     et on masque le dégradé WebGL fixe via la classe sur <body>. */
  bvClearCountdown();
  if (root) root.classList.remove('bv-mode-screens');
  document.body.classList.add('bv-engine-active');

  /* Loader avec barre de progression */
  root.innerHTML =
    '<div class="bv-loader-overlay" id="bvLoader">' +
      '<span class="bv-loader-logo">Bisou Rivage</span>' +
      '<div class="bv-loader-bar"><div class="bv-loader-fill" id="bvLoaderFill"></div></div>' +
      '<span class="bv-loader-msg">' + esc(CFG.lang.loader_msg) + '</span>' +
    '</div>';
  var fill = document.getElementById('bvLoaderFill');
  if (fill) setTimeout(function() { fill.style.width = '100%'; }, 30);

  preloadPhotos(function() {
    var loader = document.getElementById('bvLoader');
    if (loader) {
      loader.classList.add('fade-out');
      setTimeout(function() {
        root.innerHTML = buildVoteHTML();
        bindAllEvents();
        buildPillDOM();
        refresh();
        applyLayout();
        if (isDesktop()) openViewer(0, 'vote');
      }, 500);
    } else {
      root.innerHTML = buildVoteHTML();
      bindAllEvents();
      buildPillDOM();
      refresh();
      applyLayout();
      if (isDesktop()) openViewer(0, 'vote');
    }
  });
}

init();

})();
/* redesign mobile vote — 2026-06-09 */
